import { useCallback, useEffect, useMemo, useState } from "react";
import { adminApi } from "../services/api";
import { useAuth } from "../hooks/useAuth";
import { useToastActions } from "../components/shared/Toast";
import SavingNotice from "../components/shared/SavingNotice";
import useMinLoad from "../hooks/useMinLoad";

const PANELS = [
  { section: "Overview", items: [
    { key: "dashboard",      label: "Dashboard",          icon: "◈" },
    { key: "revenue",        label: "Revenue",             icon: "$" },
    { key: "intelligence",   label: "Intelligence Engine", icon: "AI" },
  ]},
  { section: "Users", items: [
    { key: "users",          label: "All Users",           icon: "◉" },
    { key: "user-detail",    label: "User Detail",         icon: "★" },
    { key: "households",     label: "Households",          icon: "⌂" },
    { key: "bulk-actions",   label: "Bulk Actions",        icon: "▣" },
    { key: "user-flags",     label: "Flags & CRM Notes",   icon: "⚑" },
  ]},
  { section: "Subscriptions", items: [
    { key: "subscriptions",  label: "All Plans",           icon: "↻" },
    { key: "trials",         label: "Trials",              icon: "T" },
    { key: "churn",          label: "At Risk",             icon: "!" },
    { key: "failed-payments",label: "Failed Payments",     icon: "✗" },
    { key: "refunds",        label: "Refunds",             icon: "⟲" },
  ]},
  { section: "Intelligence & AI", items: [
    { key: "ai-usage",       label: "AI Usage & Cost",     icon: "✦" },
    { key: "intel-analytics",label: "Rule Analytics",      icon: "▤" },
  ]},
  { section: "Operations", items: [
    { key: "support",        label: "Support Inbox",       icon: "?" },
    { key: "broadcast",      label: "Broadcast",           icon: ">" },
    { key: "modules",        label: "Module Health",       icon: "▦" },
    { key: "discovery-health", label: "Discovery Health",   icon: "⌕" },
    { key: "feature-flags",  label: "Feature Flags",       icon: "⊟" },
    { key: "logs",           label: "Audit Logs",          icon: "#" },
  ]},
  { section: "System", items: [
    { key: "system-health",  label: "System Health",       icon: "♥" },
    { key: "compliance",     label: "Compliance / GDPR",   icon: "⚖" },
    { key: "security",       label: "Security & Roles",    icon: "🔒" },
    { key: "settings",       label: "Settings",            icon: "⚙" },
  ]},
];

const PANEL_TITLES = {
  dashboard:        "Dashboard Overview",
  revenue:          "Revenue & Financials",
  intelligence:     "Intelligence Engine",
  users:            "User Management",
  "user-detail":    "Account Detail",
  households:       "Household Management",
  "bulk-actions":   "Bulk Actions",
  "user-flags":     "User Flags & CRM Notes",
  subscriptions:    "Subscription Management",
  trials:           "Active Trials",
  churn:            "At-Risk Users",
  "failed-payments":"Failed Payments",
  refunds:          "Refunds",
  "ai-usage":       "AI Usage & Cost Tracking",
  "intel-analytics":"Intelligence Rule Analytics",
  support:          "Support Inbox",
  broadcast:        "Broadcast Center",
  modules:          "Module Health",
  "discovery-health":"Discovery Health",
  "feature-flags":  "Feature Flags & Config",
  logs:             "Audit Log",
  "system-health":  "System Health",
  compliance:       "Compliance & GDPR",
  security:         "Security & Roles",
  settings:         "System Settings",
};

const ACCESS_SOURCES = ["none", "manual", "promo", "trial", "stripe"];

// ── Estimated AI cost per provider (USD per request, rough approximation) ──
const AI_COST_PER_REQUEST = { anthropic: 0.0045, openai: 0.003, default: 0.004 };

function fmtDate(value, compact = false) {
  if (!value) return "none";
  try {
    return new Date(value).toLocaleDateString([], compact
      ? { month: "short", day: "numeric" }
      : { year: "numeric", month: "short", day: "numeric" });
  } catch { return value; }
}

function fmtTime(value) {
  if (!value) return "none";
  try {
    return new Date(value).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return value; }
}

function money(value, digits = 0) {
  return Number(value || 0).toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: digits });
}

function daysUntil(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return Math.ceil((date.getTime() - Date.now()) / 86400000);
}

function downloadJson(filename, data) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url; anchor.download = filename;
  document.body.appendChild(anchor); anchor.click();
  anchor.remove(); URL.revokeObjectURL(url);
}

function Badge({ children, type = "free" }) {
  return <span className={`hmadmin-badge hmadmin-badge-${type}`}>{children}</span>;
}

function Kpi({ label, value, sub, tone = "teal" }) {
  return (
    <div className="hmadmin-kpi" style={{ "--kpi-color": `var(--hmadmin-${tone})` }}>
      <div className="hmadmin-kpi-label">{label}</div>
      <div className="hmadmin-kpi-value">{value}</div>
      <div className="hmadmin-kpi-sub">{sub}</div>
    </div>
  );
}

function Card({ title, action, children }) {
  return (
    <section className="hmadmin-card">
      <div className="hmadmin-card-header">
        <div className="hmadmin-card-title">{title}</div>
        {action}
      </div>
      <div className="hmadmin-card-body">{children}</div>
    </section>
  );
}

function Modal({ title, tone, children, footer, onClose }) {
  if (!title) return null;
  return (
    <div className="hmadmin-modal-overlay" onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div className="hmadmin-modal">
        <div className="hmadmin-modal-header">
          <div className={tone === "danger" ? "is-danger" : ""}>{title}</div>
          <button type="button" onClick={onClose}>×</button>
        </div>
        <div className="hmadmin-modal-body">{children}</div>
        <div className="hmadmin-modal-footer">{footer}</div>
      </div>
    </div>
  );
}

function firstInitial(name, email) {
  return String(name || email || "?").trim().charAt(0).toUpperCase();
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const { addToast, updateToast } = useToastActions();
  const [panel, setPanel] = useState("dashboard");
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [busyMessage, setBusyMessage] = useState("");
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [query, setQuery] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  // Bulk actions state
  const [bulkSelected, setBulkSelected] = useState(new Set());
  // Feature flags state
  const [featureFlags, setFeatureFlags] = useState(null);
  // GDPR queue state
  const [gdprQueue, setGdprQueue] = useState(null);
  const [discoveryHealth, setDiscoveryHealth] = useState(null);
  const [securityEvents, setSecurityEvents] = useState(null);
  // User notes (lazy-loaded per user)
  const [userNotes, setUserNotes] = useState({});
  // Confirmation gate — every destructive action passes through this
  const [confirmAction, setConfirmAction] = useState(null);
  // confirmAction shape: { title, message, danger, label, onConfirm }

  const loadAdmin = useCallback(async (background = false) => {
    if (!background) setLoading(true);
    setError("");
    try {
      const [overview, healthResult, noticeResult] = await Promise.all([
        adminApi.overview(),
        adminApi.health(),
        adminApi.getSystemNotice(),
      ]);
      setData(overview);
      setHealth(healthResult);
      setNotice(noticeResult.notice || null);
      setSelectedUserId(current =>
        overview.users?.some(item => item.id === current)
          ? current
          : overview.users?.[0]?.id || null
      );
    } catch (err) {
      setError(err.message || "Admin console failed to load.");
    } finally {
      if (!background) setLoading(false);
    }
  }, []);

  useEffect(() => { loadAdmin(); }, [loadAdmin]);
  useEffect(() => {
    const onKey = e => {
      if (e.key === "Escape") { setModal(null); setConfirmAction(null); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Lazy-load feature flags when navigating to that panel
  useEffect(() => {
    if (panel === "feature-flags" && !featureFlags) {
      adminApi.getFeatureFlags().then(r => setFeatureFlags(r)).catch(() => setFeatureFlags([]));
    }
    if (panel === "compliance" && !gdprQueue) {
      adminApi.gdprQueue().then(r => setGdprQueue(r?.requests || [])).catch(() => setGdprQueue([]));
    }
    if (panel === "discovery-health" && !discoveryHealth) {
      adminApi.discoverySyncHealth().then(r => setDiscoveryHealth(r)).catch(() => setDiscoveryHealth({ sources: [], summary: {}, recent_errors: [] }));
    }
    if (panel === "security" && !securityEvents) {
      adminApi.securityEvents(100).then(r => setSecurityEvents(r)).catch(() => setSecurityEvents([]));
    }
  }, [panel, featureFlags, gdprQueue, discoveryHealth, securityEvents]);

  const refreshDiscoveryHealth = useCallback(async () => {
    const result = await adminApi.discoverySyncHealth();
    setDiscoveryHealth(result);
    return result;
  }, []);

  async function run(key, loadingText, successText, action, reload = true) {
    const toastId = addToast(loadingText, "loading", { persist: true });
    setBusy(key); setBusyMessage(loadingText); setError(""); setStatus("");
    try {
      const result = await action();
      if (reload) await loadAdmin(true);
      updateToast(toastId, successText, "success");
      setStatus(successText);
      window.setTimeout(() => setStatus(""), 4000);
      return result;
    } catch (err) {
      updateToast(toastId, err.message || "Admin action failed", "error");
      setError(err.message || "Admin action failed");
      return null;
    } finally {
      setBusy(""); setBusyMessage("");
    }
  }

  // Gate every destructive action through a confirmation modal.
  // Usage: withConfirm({ title, message, danger, label, onConfirm })
  function withConfirm({ title, message, danger = false, label = "Confirm", onConfirm }) {
    setConfirmAction({ title, message, danger, label, onConfirm });
  }

  const summary = data?.summary || {};
  const users = useMemo(() => data?.users || [], [data?.users]);
  const workspaces = useMemo(() => data?.workspaces || [], [data?.workspaces]);
  const insights = useMemo(() => data?.intelligence_insights || [], [data?.intelligence_insights]);
  const logs = useMemo(() => data?.admin_audit_logs || [], [data?.admin_audit_logs]);
  const billingLogs = useMemo(() => data?.logs || [], [data?.logs]);
  const subscriptions = useMemo(() => data?.subscriptions || [], [data?.subscriptions]);
  const support = useMemo(() => data?.support_requests || [], [data?.support_requests]);
  const selectedUser = users.find(item => item.id === selectedUserId) || users[0] || null;
  const selectedCounts = selectedUser?.module_counts || {};

  const filteredUsers = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return users;
    return users.filter(item =>
      String(item.id).includes(needle) ||
      (item.full_name || "").toLowerCase().includes(needle) ||
      (item.email || "").toLowerCase().includes(needle)
    );
  }, [users, query]);

  const activeTrials = users.filter(item => item.access_source === "trial" || item.access_expires_at);
  const atRisk = users.filter(item => {
    const remaining = daysUntil(item.access_expires_at);
    return item.stripe_subscription_status === "canceled" ||
      item.stripe_subscription_status === "paused" ||
      (remaining !== null && remaining <= 7);
  });

  const failedPayments = billingLogs.filter(l => l.status === "failed" || l.event_type?.includes("fail"));

  // AI usage derived metrics
  const aiMetrics = useMemo(() => {
    const totalRequests = users.reduce((s, u) => s + (u.ai_requests_used || 0), 0);
    const totalLimit = users.reduce((s, u) => s + (u.ai_requests_limit || 0), 0);
    const estimatedCost = totalRequests * AI_COST_PER_REQUEST.default;
    const byPlan = { pro: 0, free: 0, family: 0, trial: 0 };
    users.forEach(u => {
      const plan = u.personal_plan_type === "pro" ? "pro" : u.access_source === "trial" ? "trial" : "free";
      byPlan[plan] = (byPlan[plan] || 0) + (u.ai_requests_used || 0);
    });
    const topConsumers = [...users].sort((a, b) => (b.ai_requests_used || 0) - (a.ai_requests_used || 0)).slice(0, 10);
    return { totalRequests, totalLimit, estimatedCost, byPlan, topConsumers };
  }, [users]);

  // Intelligence rule analytics
  const ruleMetrics = useMemo(() => {
    const ruleCounts = {};
    insights.forEach(ins => {
      const key = ins.rule_id || ins.title || "unknown";
      ruleCounts[key] = ruleCounts[key] || { rule_id: key, count: 0, savings: 0, dismissed: 0, acted_on: 0 };
      ruleCounts[key].count += 1;
      ruleCounts[key].savings += Number(ins.savings_amount || 0);
      if (ins.is_dismissed) ruleCounts[key].dismissed += 1;
      if (ins.was_acted_on) ruleCounts[key].acted_on += 1;
    });
    return Object.values(ruleCounts).sort((a, b) => b.count - a.count);
  }, [insights]);

  const moduleRows = [
    ["Food", summary.pantry_items || 0, `${summary.meal_plans || 0} meal plans`, "teal"],
    ["Events & Trips", (summary.personal_events || 0) + (summary.group_events || 0), "planned trips/events", "blue"],
    ["People & Gifts", summary.people || 0, `${summary.registries || 0} registries`, "purple"],
    ["Home Vault", summary.records || 0, "records stored", "amber"],
    ["Vehicles", summary.vehicles || 0, "vehicles tracked", "green"],
    ["Subscriptions", summary.subscriptions || 0, "active subscriptions", "red"],
    ["Utilities", summary.utilities || 0, "utility bills", "blue"],
  ];

  const title = PANEL_TITLES[panel] || "Admin";

  function openModal(type, target = selectedUser) {
    setForm({
      userId: target?.id,
      email: target?.email || "",
      plan: target?.personal_plan_type || "none",
      source: target?.access_source || "none",
      expiry: target?.access_expires_at ? target.access_expires_at.slice(0, 10) : "",
      days: 14, months: 1,
      reason: "Admin action",
      subject: "A note from HouseMind",
      body: "",
      notice_type: "info",
      message: "",
      audience: "all",
      flag_type: "review",
      note: "",
      refund_amount: "",
      refund_reason: "",
      flag_key: "",
      flag_value: "",
    });
    setModal(type);
  }

  function expiryIso(value) {
    return value ? new Date(`${value}T12:00:00`).toISOString() : null;
  }

  async function submitModal() {
    const targetId = form.userId;
    if (modal === "plan") {
      await run("plan", "Updating plan...", "Plan updated", () => adminApi.updateUserAccess(targetId, {
        personal_plan_type: form.plan,
        access_source: form.source,
        access_expires_at: expiryIso(form.expiry),
      }));
    }
    if (modal === "trial") {
      await run("trial", "Extending trial...", "Trial updated", () => adminApi.extendTrial(targetId, { days: Number(form.days || 14), reason: form.reason }));
    }
    if (modal === "cancel") {
      await run("cancel", "Cancelling...", "Subscription cancelled", () => adminApi.cancelSubscription(targetId, { reason: form.reason }));
    }
    if (modal === "pause") {
      await run("pause", "Pausing...", "Subscription paused", () => adminApi.pauseSubscription(targetId, { months: Number(form.months || 1) }));
    }
    if (modal === "email") {
      await run("email", "Sending email...", "Email sent", () => adminApi.emailUser(targetId, { subject: form.subject, body: form.body }), false);
    }
    if (modal === "delete") {
      if (form.confirm !== form.email) { setError("Type the user's email exactly to confirm deletion."); return; }
      await run("delete", "Deleting account...", "Account deleted", () => adminApi.deleteUser(targetId));
    }
    if (modal === "broadcast") {
      await run("broadcast", "Sending broadcast...", "Broadcast sent", () => adminApi.broadcastEmail({ audience: form.audience, subject: form.subject, body: form.body }), false);
    }
    if (modal === "notice") {
      await run("notice", "Publishing notice...", "Notice published", async () => {
        const result = await adminApi.setSystemNotice({ notice_type: form.notice_type, message: form.message });
        setNotice(result.notice || null);
      }, false);
    }
    if (modal === "flag") {
      await run("flag", "Flagging user...", "User flagged", () => adminApi.flagUser(targetId, { flag_type: form.flag_type, note: form.note }));
    }
    if (modal === "add-note") {
      await run("note", "Saving note...", "Note saved", async () => {
        await adminApi.addUserNote(targetId, { note: form.note });
        const notes = await adminApi.getUserNotes(targetId);
        setUserNotes(n => ({ ...n, [targetId]: notes?.notes || [] }));
      }, false);
    }
    if (modal === "refund") {
      withConfirm({
        title: `Record refund for ${form.email}?`,
        message: `This records a refund of ${form.refund_amount || "the entered amount"}. Confirm the Stripe refund has been created or is about to be created; this admin action only records the HouseMind-side note.`,
        danger: true,
        label: "Record refund",
        onConfirm: async () => {
          await run("refund", "Recording refund...", "Refund recorded", () => adminApi.recordRefund(targetId, { amount: form.refund_amount, reason: form.refund_reason }));
          setModal(null);
        },
      });
      return;
    }
    if (modal === "feature-flag") {
      withConfirm({
        title: `Update feature flag "${form.flag_key}"?`,
        message: `This will set the flag value to "${form.flag_value}". Feature flags can immediately change behavior for users or internal limits.`,
        label: "Update flag",
        onConfirm: async () => {
          await run("feature-flag", "Saving flag...", "Feature flag updated", async () => {
            await adminApi.setFeatureFlag({ key: form.flag_key, value: form.flag_value });
            const flags = await adminApi.getFeatureFlags();
            setFeatureFlags(flags);
          }, false);
          setModal(null);
        },
      });
      return;
    }
    if (modal === "gdpr-export") {
      await run("gdpr-export", "Generating export...", "Export triggered", () => adminApi.gdprExportUser(targetId), false);
    }
    if (modal === "impersonate") {
      withConfirm({
        title: `Login as ${form.email}?`,
        message: "This creates an audited impersonation session and opens it in a new tab. Use only for support cases where account access is necessary.",
        danger: true,
        label: "Open user session",
        onConfirm: async () => {
          const handoffWindow = window.open("about:blank", "_blank");
          try {
            handoffWindow?.document?.write?.("<!doctype html><title>HouseMind</title><body style=\"font-family:system-ui;padding:24px\">Creating impersonation session...</body>");
          } catch {}
          const result = await run("impersonate", "Generating session...", "Impersonation session opened in a new tab", async () => {
            const result = await adminApi.impersonateUser(targetId);
            if (!result?.token) throw new Error("No impersonation token returned. Please check backend logs.");
            const handoffUrl = `${window.location.origin}/impersonate?token=${encodeURIComponent(result.token)}`;
            try {
              await navigator.clipboard?.writeText?.(result.token);
            } catch {}
            console.warn("[ADMIN IMPERSONATE] Token:", result.token);
            if (handoffWindow && !handoffWindow.closed) {
              handoffWindow.location.replace(handoffUrl);
            } else {
              const opened = window.open(handoffUrl, "_blank", "noopener,noreferrer");
              if (!opened) {
                throw new Error("Popup blocked. Token copied when possible; paste it manually in an incognito session.");
              }
            }
            return result;
          }, false);
          if (!result && handoffWindow && !handoffWindow.closed) {
            handoffWindow.close();
          }
          setModal(null);
        },
      });
      return;
    }
    if (modal === "bulk-email") {
      const ids = Array.from(bulkSelected);
      withConfirm({
        title: `Send bulk email to ${ids.length} user${ids.length !== 1 ? "s" : ""}?`,
        message: `This will email ${ids.length} selected recipient${ids.length !== 1 ? "s" : ""}. Confirm the audience and message are correct before sending.`,
        label: "Send bulk email",
        onConfirm: async () => {
          await run("bulk-email", `Sending to ${ids.length} users...`, `Email sent to ${ids.length} users`, async () => {
            for (const id of ids) {
              await adminApi.emailUser(id, { subject: form.subject, body: form.body });
            }
          }, false);
          setModal(null);
        },
      });
      return;
    }
    setModal(null);
  }

  async function exportData(kind) {
    await run(`export-${kind}`, `Preparing ${kind} export...`, "Export ready", async () => {
      const payload = kind === "users" ? await adminApi.exportUsers() : await adminApi.exportBilling();
      downloadJson(`housemind-${kind}-export.json`, payload);
    }, false);
  }

  async function refreshAdmin() {
    await run("refresh", "Refreshing...", "Admin console refreshed", () => loadAdmin(true), false);
  }

  async function loadUserNotes(userId) {
    if (userNotes[userId]) return;
    try {
      const notes = await adminApi.getUserNotes(userId);
      setUserNotes(n => ({ ...n, [userId]: notes?.notes || [] }));
    } catch { setUserNotes(n => ({ ...n, [userId]: [] })); }
  }

  const showActionNotice = useMinLoad(!!busy, 700);

  if (loading) {
    return (
      <div className="hmadmin-wrap">
        <Style />
        <div className="hmadmin-loading">Loading HouseMind Admin...</div>
      </div>
    );
  }

  return (
    <div className="hmadmin-wrap">
      <Style />
      {showActionNotice && <SavingNotice title="Working" message={busyMessage || "Updating admin console..."} />}
      <aside className="hmadmin-sidebar">
        <div className="hmadmin-sb-header">
          <div className="hmadmin-logo"><span />HouseMind Admin</div>
          <div className="hmadmin-sb-badge">CONTROL CENTER</div>
          <div className="hmadmin-admin-info">
            <div>{user?.email || "admin"}</div>
            <small>Super Administrator</small>
          </div>
        </div>
        <nav className="hmadmin-sb-nav">
          {PANELS.map(group => (
            <div key={group.section}>
              <div className="hmadmin-sb-section">{group.section}</div>
              {group.items.map(item => {
                const count =
                  item.key === "trials" ? activeTrials.length :
                  item.key === "churn" ? atRisk.length :
                  item.key === "support" ? summary.open_support_requests :
                  item.key === "failed-payments" ? failedPayments.length :
                  item.key === "discovery-health" ? discoveryHealth?.summary?.sync_error :
                  null;
                return (
                  <button key={item.key} type="button"
                    className={`hmadmin-sb-item${panel === item.key ? " active" : ""}`}
                    onClick={() => setPanel(item.key)}>
                    <span>{item.icon}</span>{item.label}
                    {!!count && <em>{count}</em>}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>
        <div className="hmadmin-sb-footer">v1.0.0 · production</div>
      </aside>

      <main className="hmadmin-main">
        <header className="hmadmin-topbar">
          <strong>{title}</strong>
          <div>
            <span className="hmadmin-live"><i />API {health?.status || "unknown"}</span>
            <span>{new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
            <button className="hmadmin-btn ghost xs" onClick={refreshAdmin}>↻ Refresh</button>
          </div>
        </header>

        <div className="hmadmin-content">
          {error && <div className="hmadmin-alert">{error}</div>}
          {status && <div className="hmadmin-alert success">{status}</div>}

          {/* ── DASHBOARD ── */}
          {panel === "dashboard" && (
            <>
              <div className="hmadmin-kpi-grid">
                <Kpi label="Total Users" value={summary.user_count || 0} sub={`+${summary.new_users_7d || 0} this week`} />
                <Kpi label="MRR Estimate" value={money(summary.mrr_estimate)} sub={`${summary.active_personal_pro || 0} pro · ${summary.active_family_workspaces || 0} family`} tone="green" />
                <Kpi label="Active Insights" value={summary.active_insights || 0} sub={`${summary.insights_today || 0} generated today`} tone="purple" />
                <Kpi label="Open Support" value={summary.open_support_requests || 0} sub={`${support.length} total loaded`} tone="amber" />
              </div>
              <div className="hmadmin-grid-6040">
                <Card title="Recent Users" action={<button className="hmadmin-btn ghost xs" onClick={() => setPanel("users")}>View all</button>}>
                  <Table headers={["User", "Plan", "AI", "Joined", "Actions"]}>
                    {users.slice(0, 8).map(item => <UserRow key={item.id} user={item} onSelect={() => { setSelectedUserId(item.id); setPanel("user-detail"); }} onModal={openModal} />)}
                  </Table>
                </Card>
                <Card title="Operations">
                  <div className="hmadmin-action-list">
                    <Action title="Run intelligence engine" text="Process cross-module household insights for all users." action="Run all" onClick={() => withConfirm({ title: "Run intelligence engine?", message: "This will process cross-module rules for all active users and generate fresh household insights. The job runs in the background.", label: "Run now", onConfirm: () => run("intel", "Running intelligence...", "Intelligence run complete", () => adminApi.runIntelligenceAll()) })} />
                    <Action title="Run reminders" text="Trigger the reminder email job now." action="Run" onClick={() => withConfirm({ title: "Send reminder emails now?", message: "This will immediately trigger reminder emails for all users with upcoming occasions. Only send if the scheduled job has not already run today.", label: "Send reminders", onConfirm: () => run("reminders", "Running reminders...", "Reminder job complete", () => adminApi.runReminders()) })} />
                    <Action title="Run discovery sync" text="Refresh connected inbox and calendar sources into review-only import candidates." action="Run" onClick={() => withConfirm({ title: "Run discovery sync now?", message: "This checks connected discovery sources and creates review candidates only. It will not write directly into household modules.", label: "Run sync", onConfirm: () => run("discovery-sync", "Running discovery sync...", "Discovery sync complete", () => adminApi.runDiscoverySync()) })} />
                    <Action title="Refresh console" text="Reload health, metrics, users, insights, support, and logs." action="Refresh" onClick={refreshAdmin} />
                  </div>
                </Card>
              </div>
              <div className="hmadmin-grid2">
                <InsightList insights={insights.filter(i => !i.is_dismissed).slice(0, 8)} />
                <Timeline logs={[...logs, ...billingLogs].slice(0, 8)} />
              </div>
            </>
          )}

          {/* ── REVENUE ── */}
          {panel === "revenue" && (
            <div className="hmadmin-grid-6040">
              <Card title="Revenue Snapshot">
                <div className="hmadmin-revenue-big">{money(summary.mrr_estimate)}</div>
                <div className="hmadmin-muted">Projected ARR: {money((summary.mrr_estimate || 0) * 12)}</div>
                <div className="hmadmin-chart">
                  {[38, 44, 48, 53, 61, 68, 74, 82, 88, 92, 96, 100].map((h, i) => <span key={i} style={{ height: `${h}%` }} />)}
                </div>
              </Card>
              <Card title="Plan Mix">
                <div className="hmadmin-stat-row three">
                  <MiniStat label="Pro" value={summary.active_personal_pro || 0} />
                  <MiniStat label="Family" value={summary.active_family_workspaces || 0} />
                  <MiniStat label="Stripe Linked" value={summary.stripe_linked || 0} />
                </div>
                <div style={{ padding: "0 20px 16px", display: "flex", gap: 8 }}>
                  <button className="hmadmin-btn ghost" onClick={() => exportData("billing")}>Export billing</button>
                  <button className="hmadmin-btn ghost" onClick={() => exportData("users")}>Export users</button>
                </div>
              </Card>
              <Card title="Recent Billing Events">
                <Table headers={["Event", "Entity", "Status", "Time"]}>
                  {billingLogs.slice(0, 12).map(log => (
                    <tr key={log.id}>
                      <td>{log.event_type}</td>
                      <td>{log.entity_type || "system"} {log.entity_id || ""}</td>
                      <td><Badge type={log.status === "failed" ? "cancelled" : "active"}>{log.status}</Badge></td>
                      <td className="mono">{fmtTime(log.created_at)}</td>
                    </tr>
                  ))}
                </Table>
              </Card>
            </div>
          )}

          {/* ── INTELLIGENCE ENGINE ── */}
          {panel === "intelligence" && (
            <Card title="Intelligence Engine" action={<button className="hmadmin-btn primary xs" onClick={() => withConfirm({ title: "Run intelligence engine for all users?", message: "This will re-run all cross-module rules and regenerate household insights for every active user. It may take 30–60 seconds.", label: "Run now", onConfirm: () => run("intel-panel", "Running intelligence...", "Intelligence run complete", () => adminApi.runIntelligenceAll()) })}>Run all</button>}>
              <div className="hmadmin-stat-row">
                <MiniStat label="Events" value={health?.household_events || 0} />
                <MiniStat label="Pending" value={health?.pending_insights || 0} />
                <MiniStat label="Today" value={summary.insights_today || 0} />
                <MiniStat label="Savings Found" value={money(summary.total_savings)} />
              </div>
              <InsightRows insights={insights} />
            </Card>
          )}

          {/* ── USERS ── */}
          {panel === "users" && (
            <Card title={`All Users — ${filteredUsers.length}`} action={<input className="hmadmin-search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search users..." />}>
              <Table headers={["User", "Plan", "Source", "Modules", "AI Usage", "Actions"]}>
                {filteredUsers.map(item => <UserRow key={item.id} user={item} onSelect={() => { setSelectedUserId(item.id); setPanel("user-detail"); }} onModal={openModal} full />)}
              </Table>
            </Card>
          )}

          {/* ── USER DETAIL ── */}
          {panel === "user-detail" && selectedUser && (
            <>
              <div className="hmadmin-user-hero">
                <div className="hmadmin-avatar">{firstInitial(selectedUser.full_name, selectedUser.email)}</div>
                <div>
                  <h2>{selectedUser.full_name}</h2>
                  <div className="hmadmin-user-email">{selectedUser.email}</div>
                  <div className="hmadmin-tags">
                    <Badge type={selectedUser.personal_plan_type === "pro" ? "pro" : "free"}>{selectedUser.personal_plan_type === "pro" ? "Pro" : "Free"}</Badge>
                    <Badge type={selectedUser.access_source === "trial" ? "trial" : "active"}>{selectedUser.access_source}</Badge>
                    {selectedUser.email === user?.email && <Badge type="admin">Admin</Badge>}
                  </div>
                </div>
                <div className="hmadmin-user-actions">
                  <button className="hmadmin-btn primary" onClick={() => openModal("plan", selectedUser)}>Change Plan</button>
                  <button className="hmadmin-btn blue" onClick={() => openModal("email", selectedUser)}>Email User</button>
                  <button className="hmadmin-btn ghost" onClick={() => openModal("impersonate", selectedUser)}>Login As User</button>
                  <button className="hmadmin-btn" onClick={() => openModal("flag", selectedUser)}>⚑ Flag</button>
                  <button className="hmadmin-btn danger" onClick={() => openModal("delete", selectedUser)}>Delete Account</button>
                </div>
              </div>
              <div className="hmadmin-stat-row">
                <MiniStat label="Food" value={selectedCounts.pantry_items || 0} />
                <MiniStat label="People" value={selectedCounts.people || 0} />
                <MiniStat label="Vault" value={selectedCounts.records || 0} />
                <MiniStat label="Vehicles" value={selectedCounts.vehicles || 0} />
                <MiniStat label="Subs" value={selectedCounts.subscriptions || 0} />
                <MiniStat label="Utilities" value={selectedCounts.utilities || 0} />
                <MiniStat label="Insights" value={selectedCounts.insights || 0} />
              </div>
              <div className="hmadmin-grid2">
                <Card title="Account Controls">
                  <div className="hmadmin-action-list">
                    <Action title="Extend trial / grant access" text={`Current expiry: ${fmtDate(selectedUser.access_expires_at)}`} action="Extend" onClick={() => openModal("trial", selectedUser)} />
                    <Action title="Reset AI usage" text={`${selectedUser.ai_requests_used || 0}/${selectedUser.ai_requests_limit || 0} requests used — cannot be undone`} action="Reset" onClick={() => withConfirm({ title: `Reset AI usage for ${selectedUser.full_name}?`, message: `This will set their AI request counter back to 0/${selectedUser.ai_requests_limit || 0}. The user will immediately get their full monthly allocation back. This cannot be undone.`, danger: true, label: "Reset AI usage", onConfirm: () => run("reset-ai", "Resetting AI...", "AI usage reset", () => adminApi.resetUserAiUsage(selectedUser.id)) })} />
                    <Action title="Run intelligence for this user" text="Generate fresh household insights for this account." action="Run AI" onClick={() => withConfirm({ title: `Run intelligence for ${selectedUser.full_name}?`, message: "This will re-process all cross-module rules for this user and regenerate their household insights and daily briefing.", label: "Run intelligence", onConfirm: () => run("run-user-ai", "Running intelligence...", "User intelligence complete", () => adminApi.runIntelligenceUser(selectedUser.id)) })} />
                    <Action title="GDPR data export" text="Generate full data export for this user." action="Export" onClick={() => openModal("gdpr-export", selectedUser)} />
                    <Action title="Pause subscription" text="Temporarily pause access/subscription." action="Pause" onClick={() => openModal("pause", selectedUser)} />
                    <Action title="Cancel subscription" text="Mark subscription cancelled with audit reason." action="Cancel" danger onClick={() => openModal("cancel", selectedUser)} />
                  </div>
                </Card>
                <Card title="Stripe Fields">
                  <div className="hmadmin-detail-list">
                    <Detail label="Customer" value={selectedUser.stripe_customer_id || "none"} />
                    <Detail label="Subscription" value={selectedUser.stripe_subscription_id || "none"} />
                    <Detail label="Status" value={selectedUser.stripe_subscription_status || "none"} />
                    <Detail label="Mode" value={selectedUser.stripe_mode || "none"} />
                  </div>
                </Card>
              </div>
              <Card title="CRM Notes" action={
                <button className="hmadmin-btn primary xs" onClick={() => { loadUserNotes(selectedUser.id); openModal("add-note", selectedUser); }}>+ Add Note</button>
              }>
                {(userNotes[selectedUser.id] || []).length === 0
                  ? <div className="hmadmin-empty" onClick={() => loadUserNotes(selectedUser.id)} style={{ cursor: "pointer" }}>No notes. Click to load or add the first note.</div>
                  : (userNotes[selectedUser.id] || []).map((note, i) => (
                    <div key={i} className="hmadmin-insight">
                      <div><strong>{note.created_by || "Admin"}</strong><p>{note.text || note.note}</p><span className="mono">{fmtTime(note.created_at)}</span></div>
                    </div>
                  ))
                }
              </Card>
            </>
          )}

          {/* ── HOUSEHOLDS ── */}
          {panel === "households" && (
            <Card title={`Households / Workspaces — ${workspaces.length}`}>
              <Table headers={["Workspace", "Owner", "Plan", "Members", "Modules", "AI"]}>
                {workspaces.map(ws => (
                  <tr key={ws.id}>
                    <td><div className="name">{ws.name}</div><div className="muted">{ws.workspace_type}</div></td>
                    <td>{ws.owner_name}</td>
                    <td><Badge type={ws.plan === "family" ? "family" : "free"}>{ws.plan}</Badge></td>
                    <td>{ws.member_count}</td>
                    <td>Food {ws.module_counts?.pantry_items || 0} · Vault {ws.module_counts?.records || 0} · Vehicles {ws.module_counts?.vehicles || 0}</td>
                    <td className="mono">{ws.ai_requests_used || 0}/{ws.ai_requests_limit || 0}</td>
                  </tr>
                ))}
              </Table>
            </Card>
          )}

          {/* ── BULK ACTIONS ── */}
          {panel === "bulk-actions" && (
            <>
              <div className="hmadmin-grid2" style={{ marginBottom: 16 }}>
                <Card title="Select Users">
                  <div style={{ padding: "12px 20px", borderBottom: "1px solid var(--hmadmin-border)", display: "flex", gap: 8 }}>
                    <input className="hmadmin-search" style={{ flex: 1 }} placeholder="Filter users..." value={query} onChange={e => setQuery(e.target.value)} />
                    <button className="hmadmin-btn ghost xs" onClick={() => setBulkSelected(new Set(filteredUsers.map(u => u.id)))}>Select all</button>
                    <button className="hmadmin-btn ghost xs" onClick={() => setBulkSelected(new Set())}>Clear</button>
                  </div>
                  <div style={{ maxHeight: 320, overflowY: "auto" }}>
                    {filteredUsers.map(u => (
                      <label key={u.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 20px", borderBottom: "1px solid var(--hmadmin-border)", cursor: "pointer" }}>
                        <input type="checkbox" checked={bulkSelected.has(u.id)} onChange={e => {
                          setBulkSelected(prev => {
                            const next = new Set(prev);
                            e.target.checked ? next.add(u.id) : next.delete(u.id);
                            return next;
                          });
                        }} />
                        <span style={{ flex: 1 }}><span className="name">{u.full_name}</span><span className="email" style={{ marginLeft: 8 }}>{u.email}</span></span>
                        <Badge type={u.personal_plan_type === "pro" ? "pro" : "free"}>{u.personal_plan_type}</Badge>
                      </label>
                    ))}
                  </div>
                </Card>
                <Card title={`Bulk Actions — ${bulkSelected.size} selected`}>
                  <div className="hmadmin-action-list">
                    <Action title="Bulk email" text={`Send a custom email to all ${bulkSelected.size} selected users.`} action="Compose" onClick={() => { if (!bulkSelected.size) { setError("Select at least one user."); return; } setModal("bulk-email"); }} />
                    <Action title="Bulk plan change" text="Change plan for all selected users simultaneously." action="Change plan" onClick={() => { if (!bulkSelected.size) { setError("Select at least one user."); return; } setModal("bulk-plan"); }} />
                    <Action title="Export selected" text="Download JSON with all selected user records." action="Export" onClick={() => {
                      const selected = users.filter(u => bulkSelected.has(u.id));
                      downloadJson("housemind-bulk-export.json", selected);
                    }} />
                    <Action title="Reset AI usage (bulk)" text="Reset AI request counters for all selected users." action="Reset all" onClick={() => {
                      if (!bulkSelected.size) { setError("Select at least one user."); return; }
                      withConfirm({ title: `Reset AI usage for ${bulkSelected.size} user${bulkSelected.size !== 1 ? "s" : ""}?`, message: `This will set the AI request counter back to 0 for all ${bulkSelected.size} selected users. This cannot be undone.`, danger: true, label: `Reset ${bulkSelected.size} users`, onConfirm: async () => {
                        for (const id of bulkSelected) await adminApi.resetUserAiUsage(id);
                        await loadAdmin(true);
                        setStatus(`AI usage reset for ${bulkSelected.size} users`);
                      }});
                    }} />
                  </div>
                </Card>
              </div>
            </>
          )}

          {/* ── USER FLAGS & CRM NOTES ── */}
          {panel === "user-flags" && (
            <Card title="User Flags & Internal CRM Notes">
              <Table headers={["User", "Plan", "Flag", "Notes Count", "Actions"]}>
                {users.filter(u => u.admin_flag || u.admin_note).map(u => (
                  <tr key={u.id}>
                    <td><div className="name">{u.full_name}</div><div className="email">{u.email}</div></td>
                    <td><Badge type={u.personal_plan_type === "pro" ? "pro" : "free"}>{u.personal_plan_type}</Badge></td>
                    <td>{u.admin_flag ? <Badge type="cancelled">{u.admin_flag}</Badge> : <span className="muted">none</span>}</td>
                    <td className="mono">{u.admin_note ? "1+" : "0"}</td>
                    <td><div className="actions">
                      <button className="hmadmin-btn ghost xs" onClick={() => { setSelectedUserId(u.id); setPanel("user-detail"); }}>View</button>
                      <button className="hmadmin-btn xs" onClick={() => openModal("flag", u)}>⚑ Flag</button>
                      <button className="hmadmin-btn blue xs" onClick={() => { loadUserNotes(u.id); openModal("add-note", u); }}>+ Note</button>
                    </div></td>
                  </tr>
                ))}
              </Table>
              {users.filter(u => u.admin_flag || u.admin_note).length === 0 && (
                <div className="hmadmin-empty">No flagged users yet. Use ⚑ Flag on any user detail to mark accounts for review.</div>
              )}
            </Card>
          )}

          {/* ── SUBSCRIPTIONS ── */}
          {panel === "subscriptions" && (
            <Card title={`Subscriptions — ${subscriptions.length}`}>
              <Table headers={["Subscription", "Amount", "Cycle", "Renewal", "Status"]}>
                {subscriptions.map(sub => (
                  <tr key={sub.id}>
                    <td><div className="name">{sub.name}</div><div className="muted">{sub.provider || "no provider"} · workspace {sub.workspace_id}</div></td>
                    <td>{money(sub.monthly_amount, 2)}/mo</td>
                    <td>{sub.billing_cycle}</td>
                    <td className="mono">{fmtDate(sub.next_renewal_date, true)}</td>
                    <td><Badge type={sub.status === "active" ? "active" : "cancelled"}>{sub.status}</Badge></td>
                  </tr>
                ))}
              </Table>
            </Card>
          )}

          {/* ── TRIALS ── */}
          {panel === "trials" && (
            <Card title={`Active Trials / Expiring Access — ${activeTrials.length}`}>
              <Table headers={["User", "Days Left", "Expires", "AI Usage", "Actions"]}>
                {activeTrials.map(item => {
                  const days = daysUntil(item.access_expires_at);
                  const used = item.ai_requests_limit ? Math.round(((item.ai_requests_used || 0) / item.ai_requests_limit) * 100) : 0;
                  return (
                    <tr key={item.id}>
                      <td><div className="name">{item.full_name}</div><div className="email">{item.email}</div></td>
                      <td><Badge type={days !== null && days <= 3 ? "cancelled" : "trial"}>{days ?? "n/a"} days</Badge></td>
                      <td className="mono">{fmtDate(item.access_expires_at, true)}</td>
                      <td><Progress value={used} /></td>
                      <td><div className="actions">
                        <button className="hmadmin-btn primary xs" onClick={() => openModal("trial", item)}>Extend</button>
                        <button className="hmadmin-btn blue xs" onClick={() => openModal("email", item)}>Email</button>
                      </div></td>
                    </tr>
                  );
                })}
              </Table>
            </Card>
          )}

          {/* ── AT RISK / CHURN ── */}
          {panel === "churn" && (
            <>
              <div className="hmadmin-kpi-grid">
                <Kpi label="At-Risk Users" value={atRisk.length} sub="cancelled, paused, or expiring ≤7 days" tone="red" />
                <Kpi label="Cancelled This Month" value={billingLogs.filter(l => l.event_type?.includes("cancel")).length} sub="from billing events" tone="amber" />
                <Kpi label="Trials Expiring Soon" value={activeTrials.filter(u => (daysUntil(u.access_expires_at) || 99) <= 3).length} sub="in 3 days or less" tone="purple" />
                <Kpi label="Failed Payments" value={failedPayments.length} sub="from billing log" tone="red" />
              </div>
              <Card title={`At-Risk Users — ${atRisk.length}`}>
                <Table headers={["User", "Risk Signal", "Days Left", "Plan", "Actions"]}>
                  {atRisk.map(item => {
                    const days = daysUntil(item.access_expires_at);
                    const signal = item.stripe_subscription_status === "canceled" ? "Cancelled" :
                      item.stripe_subscription_status === "paused" ? "Paused" :
                      days !== null ? `Expires in ${days}d` : "At risk";
                    return (
                      <tr key={item.id}>
                        <td><div className="name">{item.full_name}</div><div className="email">{item.email}</div></td>
                        <td><Badge type="cancelled">{signal}</Badge></td>
                        <td className="mono">{days ?? "—"}</td>
                        <td><Badge type={item.personal_plan_type === "pro" ? "pro" : "free"}>{item.personal_plan_type}</Badge></td>
                        <td><div className="actions">
                          <button className="hmadmin-btn blue xs" onClick={() => openModal("email", item)}>Re-engage</button>
                          <button className="hmadmin-btn primary xs" onClick={() => openModal("trial", item)}>Grant access</button>
                        </div></td>
                      </tr>
                    );
                  })}
                </Table>
              </Card>
            </>
          )}

          {/* ── FAILED PAYMENTS ── */}
          {panel === "failed-payments" && (
            <>
              <div className="hmadmin-kpi-grid">
                <Kpi label="Failed Events" value={failedPayments.length} sub="from billing log" tone="red" />
                <Kpi label="Est. MRR at Risk" value={money(failedPayments.length * 9)} sub="avg $9/user estimate" tone="amber" />
                <Kpi label="Users Affected" value={new Set(failedPayments.map(l => l.entity_id)).size} sub="unique accounts" tone="purple" />
                <Kpi label="Latest Failure" value={failedPayments.length ? fmtDate(failedPayments[0]?.created_at, true) : "none"} sub="most recent" tone="red" />
              </div>
              <Card title="Failed Payment Events">
                <Table headers={["Event", "Entity", "Time", "Actions"]}>
                  {failedPayments.length ? failedPayments.map(log => {
                    const affectedUser = users.find(u => String(u.id) === String(log.entity_id));
                    return (
                      <tr key={log.id}>
                        <td><div className="name">{log.event_type}</div><div className="muted">{log.message || "Payment processing failed"}</div></td>
                        <td>{affectedUser ? <><div className="name">{affectedUser.full_name}</div><div className="email">{affectedUser.email}</div></> : <span className="muted">{log.entity_type} {log.entity_id}</span>}</td>
                        <td className="mono">{fmtTime(log.created_at)}</td>
                        <td><div className="actions">
                          {affectedUser && <button className="hmadmin-btn blue xs" onClick={() => openModal("email", affectedUser)}>Email user</button>}
                          {affectedUser && <button className="hmadmin-btn ghost xs" onClick={() => openModal("refund", affectedUser)}>Record refund</button>}
                        </div></td>
                      </tr>
                    );
                  }) : <tr><td colSpan={4}><div className="hmadmin-empty">No failed payment events found in billing log.</div></td></tr>}
                </Table>
              </Card>
            </>
          )}

          {/* ── REFUNDS ── */}
          {panel === "refunds" && (
            <div className="hmadmin-grid-6040">
              <Card title="Issue / Record a Refund">
                <div style={{ padding: 20 }}>
                  <p style={{ fontSize: 13, color: "var(--hmadmin-text2)", marginBottom: 16, lineHeight: 1.6 }}>
                    Select a user and record a refund. HouseMind does not process Stripe refunds directly — this records the refund in the audit log and adjusts the user's access if needed. Process the actual charge refund in your Stripe dashboard.
                  </p>
                  <div className="hmadmin-form-group"><label>Select User</label>
                    <select onChange={e => setSelectedUserId(Number(e.target.value))}>
                      {users.map(u => <option key={u.id} value={u.id}>{u.full_name} — {u.email}</option>)}
                    </select>
                  </div>
                  <button className="hmadmin-btn primary" onClick={() => openModal("refund", selectedUser)}>Record refund for {selectedUser?.full_name || "selected user"}</button>
                </div>
              </Card>
              <Card title="Recent Refund Records">
                <Table headers={["Admin Action", "Entity", "Time"]}>
                  {logs.filter(l => l.action?.toLowerCase().includes("refund")).map(log => (
                    <tr key={log.id}>
                      <td>{log.action}</td>
                      <td>{log.entity_type} {log.entity_id}</td>
                      <td className="mono">{fmtTime(log.created_at)}</td>
                    </tr>
                  ))}
                </Table>
                {!logs.filter(l => l.action?.toLowerCase().includes("refund")).length && (
                  <div className="hmadmin-empty">No refund records in audit log.</div>
                )}
              </Card>
            </div>
          )}

          {/* ── AI USAGE & COST ── */}
          {panel === "ai-usage" && (
            <>
              <div className="hmadmin-kpi-grid">
                <Kpi label="Total AI Requests" value={aiMetrics.totalRequests.toLocaleString()} sub={`of ${aiMetrics.totalLimit.toLocaleString()} total limit`} />
                <Kpi label="Est. Monthly Cost" value={money(aiMetrics.estimatedCost, 2)} sub="at ~$0.004/request avg" tone="amber" />
                <Kpi label="Pro Plan Requests" value={aiMetrics.byPlan.pro.toLocaleString()} sub="from pro users" tone="green" />
                <Kpi label="Trial Requests" value={aiMetrics.byPlan.trial.toLocaleString()} sub="from trial users" tone="purple" />
              </div>
              <div className="hmadmin-grid2">
                <Card title="AI Usage by Plan">
                  <div className="hmadmin-action-list">
                    {Object.entries(aiMetrics.byPlan).map(([plan, count]) => (
                      <div key={plan} className="hmadmin-action-row">
                        <div><strong style={{ textTransform: "capitalize" }}>{plan}</strong><p>{count.toLocaleString()} requests · est. {money(count * AI_COST_PER_REQUEST.default, 2)}</p></div>
                        <Progress value={aiMetrics.totalRequests ? Math.round((count / aiMetrics.totalRequests) * 100) : 0} />
                      </div>
                    ))}
                  </div>
                </Card>
                <Card title="Top AI Consumers">
                  <Table headers={["User", "Plan", "Used", "Limit", "Actions"]}>
                    {aiMetrics.topConsumers.map(u => (
                      <tr key={u.id}>
                        <td><div className="name">{u.full_name}</div><div className="email">{u.email}</div></td>
                        <td><Badge type={u.personal_plan_type === "pro" ? "pro" : "free"}>{u.personal_plan_type}</Badge></td>
                        <td className="mono">{u.ai_requests_used || 0}</td>
                        <td className="mono">{u.ai_requests_limit || 0}</td>
                        <td><button className="hmadmin-btn ghost xs" onClick={() => withConfirm({ title: `Reset AI usage for ${u.full_name}?`, message: `Resets their counter to 0/${u.ai_requests_limit || 0}. Cannot be undone.`, danger: true, label: "Reset AI usage", onConfirm: () => run("reset-ai", "Resetting...", "AI usage reset", () => adminApi.resetUserAiUsage(u.id)) })}>Reset</button></td>
                      </tr>
                    ))}
                  </Table>
                </Card>
              </div>
              <Card title="AI Infrastructure Status">
                <div className="hmadmin-action-list">
                  <Detail label="Default Provider" value={health?.ai_provider || "backend configured"} />
                  <Detail label="Anthropic API" value={health?.anthropic_status || "unknown — check backend"} />
                  <Detail label="OpenAI API" value={health?.openai_status || "unknown — check backend"} />
                  <Detail label="Failed Requests Today" value={String(health?.ai_errors_today || 0)} />
                  <Detail label="Avg Latency" value={health?.ai_avg_latency_ms ? `${health.ai_avg_latency_ms}ms` : "not tracked"} />
                </div>
              </Card>
            </>
          )}

          {/* ── INTELLIGENCE ANALYTICS ── */}
          {panel === "intel-analytics" && (
            <>
              <div className="hmadmin-kpi-grid">
                <Kpi label="Rules Tracked" value={ruleMetrics.length} sub="distinct rule IDs" />
                <Kpi label="Total Insights" value={insights.length} sub="across all users" tone="green" />
                <Kpi label="Dismissed" value={insights.filter(i => i.is_dismissed).length} sub={`${insights.length ? Math.round((insights.filter(i => i.is_dismissed).length / insights.length) * 100) : 0}% dismissal rate`} tone="amber" />
                <Kpi label="Acted On" value={insights.filter(i => i.was_acted_on).length} sub={`${insights.length ? Math.round((insights.filter(i => i.was_acted_on).length / insights.length) * 100) : 0}% action rate`} tone="green" />
              </div>
              <Card title="Rule Performance">
                <Table headers={["Rule ID", "Fires", "Dismissed", "Acted On", "Dismiss Rate", "Total Savings"]}>
                  {ruleMetrics.map(rule => (
                    <tr key={rule.rule_id}>
                      <td className="mono" style={{ fontSize: 11 }}>{rule.rule_id}</td>
                      <td className="mono">{rule.count}</td>
                      <td className="mono">{rule.dismissed}</td>
                      <td className="mono">{rule.acted_on}</td>
                      <td><Progress value={rule.count ? Math.round((rule.dismissed / rule.count) * 100) : 0} /></td>
                      <td className="mono" style={{ color: "var(--hmadmin-green)" }}>{rule.savings > 0 ? money(rule.savings) : "—"}</td>
                    </tr>
                  ))}
                </Table>
                {!ruleMetrics.length && <div className="hmadmin-empty">No insights loaded yet. Run the intelligence engine first.</div>}
              </Card>
            </>
          )}

          {/* ── SUPPORT ── */}
          {panel === "support" && (
            <Card title={`Support Requests — ${support.length}`}>
              <Table headers={["Request", "Category", "Status", "Created", "Actions"]}>
                {support.map(item => (
                  <tr key={item.id}>
                    <td><div className="name">{item.subject}</div><div className="muted">{item.full_name} · {item.email}</div><div className="muted">{item.message}</div></td>
                    <td>{item.category}</td>
                    <td><Badge type={item.status === "resolved" ? "active" : "trial"}>{item.status}</Badge></td>
                    <td className="mono">{fmtDate(item.created_at, true)}</td>
                    <td>{item.status !== "resolved" && (
                      <div className="actions">
                        <button className="hmadmin-btn primary xs" onClick={() => withConfirm({ title: "Mark support request resolved?", message: `"${item.subject}" from ${item.full_name} will be marked resolved. The user will not receive any notification — reply via email first if needed.`, label: "Mark resolved", onConfirm: () => run("support", "Resolving...", "Support resolved", () => adminApi.resolveSupportRequest(item.id)) })}>Resolve</button>
                        <button className="hmadmin-btn blue xs" onClick={() => openModal("email", users.find(u => u.email === item.email) || { id: null, email: item.email })}>Reply</button>
                      </div>
                    )}</td>
                  </tr>
                ))}
              </Table>
            </Card>
          )}

          {/* ── BROADCAST ── */}
          {panel === "broadcast" && (
            <div className="hmadmin-grid2">
              <Card title="Email Broadcast" action={<button className="hmadmin-btn primary xs" onClick={() => openModal("broadcast")}>Compose</button>}>
                <div style={{ padding: 20 }}>
                  <p className="hmadmin-muted" style={{ marginBottom: 12 }}>Send a HouseMind-branded email to segmented user groups.</p>
                  <div className="hmadmin-action-list">
                    <Action title="All users" text={`${users.length} accounts`} action="Compose" onClick={() => { setForm(f => ({ ...f, audience: "all" })); openModal("broadcast"); }} />
                    <Action title="Free users only" text={`${users.filter(u => u.personal_plan_type !== "pro").length} accounts`} action="Compose" onClick={() => { setForm(f => ({ ...f, audience: "free" })); openModal("broadcast"); }} />
                    <Action title="Pro users only" text={`${users.filter(u => u.personal_plan_type === "pro").length} accounts`} action="Compose" onClick={() => { setForm(f => ({ ...f, audience: "pro" })); openModal("broadcast"); }} />
                    <Action title="Trial users only" text={`${activeTrials.length} accounts`} action="Compose" onClick={() => { setForm(f => ({ ...f, audience: "trial" })); openModal("broadcast"); }} />
                    <Action title="At-risk / churned users" text={`${atRisk.length} accounts`} action="Compose" onClick={() => { setForm(f => ({ ...f, audience: "churned" })); openModal("broadcast"); }} />
                  </div>
                </div>
              </Card>
              <Card title="System Notice" action={<button className="hmadmin-btn primary xs" onClick={() => openModal("notice")}>Publish</button>}>
                {notice
                  ? <div className="hmadmin-notice"><strong>{notice.notice_type}</strong><p>{notice.message}</p><small>{fmtTime(notice.created_at)}</small></div>
                  : <div className="hmadmin-empty">No active notice.</div>}
                {notice && <div style={{ padding: "0 20px 16px" }}><button className="hmadmin-btn ghost xs" onClick={() => withConfirm({ title: "Clear system notice?", message: `The current notice — "${notice.message}" — will be removed from the app immediately for all users.`, danger: true, label: "Clear notice", onConfirm: () => run("clear-notice", "Clearing...", "Notice cleared", async () => { await adminApi.clearSystemNotice(); setNotice(null); }, false) })}>Clear notice</button></div>}
              </Card>
            </div>
          )}

          {/* ── MODULE HEALTH ── */}
          {panel === "modules" && (
            <>
              <div className="hmadmin-kpi-grid">
                <Kpi label="Most Used Module" value="People & Gifts" sub={`${summary.people || 0} people tracked`} />
                <Kpi label="Total Pantry Items" value={(summary.pantry_items || 0).toLocaleString()} sub="across all households" tone="green" />
                <Kpi label="Vault Records" value={(summary.records || 0).toLocaleString()} sub="warranties, manuals, appliances" tone="amber" />
                <Kpi label="Vehicles Tracked" value={(summary.vehicles || 0).toLocaleString()} sub="across all accounts" tone="blue" />
              </div>
              <div className="hmadmin-module-grid">
                {moduleRows.map(([name, count, detail, tone]) => (
                  <ModuleHealth key={name} name={name} count={count} detail={detail} tone={tone} total={Math.max(summary.user_count || 1, count || 1)} />
                ))}
              </div>
            </>
          )}

          {/* ── DISCOVERY HEALTH ── */}
          {panel === "discovery-health" && (
            <>
              <div className="hmadmin-kpi-grid">
                <Kpi label="Sources" value={discoveryHealth?.summary?.total || 0} sub={`${discoveryHealth?.summary?.connected || 0} connected`} />
                <Kpi label="Sync Errors" value={discoveryHealth?.summary?.sync_error || 0} sub={`${discoveryHealth?.summary?.sync_success || 0} healthy`} tone={(discoveryHealth?.summary?.sync_error || 0) ? "red" : "green"} />
                <Kpi label="Never Synced" value={discoveryHealth?.summary?.never_synced || 0} sub={`${discoveryHealth?.summary?.pending_setup || 0} pending setup`} tone="amber" />
                <Kpi label="Disabled" value={discoveryHealth?.summary?.disabled || 0} sub={`${discoveryHealth?.summary?.not_connected || 0} not connected`} tone="blue" />
              </div>
              <div className="hmadmin-grid-6040">
                <Card title="Discovery Operations" action={<button className="hmadmin-btn ghost xs" onClick={refreshDiscoveryHealth}>Refresh health</button>}>
                  <div className="hmadmin-action-list">
                    <Action title="Run all discovery sources" text="Checks connected inbox and calendar sources and creates review-only candidates." action="Run all" onClick={() => withConfirm({ title: "Run all discovery sources?", message: "This checks connected sources only and does not write directly to modules.", label: "Run sync", onConfirm: async () => { await run("discovery-sync", "Running discovery sync...", "Discovery sync complete", () => adminApi.runDiscoverySync(), false); await refreshDiscoveryHealth(); } })} />
                    <Action title="Recent source errors" text={`${discoveryHealth?.recent_errors?.length || 0} connector issue${(discoveryHealth?.recent_errors?.length || 0) === 1 ? "" : "s"} visible`} action="Refresh" onClick={refreshDiscoveryHealth} />
                  </div>
                </Card>
                <Card title="Provider Mix">
                  <div className="hmadmin-detail-list">
                    {Object.entries(discoveryHealth?.provider_counts || {}).length === 0
                      ? <div className="hmadmin-empty">No discovery sources loaded.</div>
                      : Object.entries(discoveryHealth.provider_counts || {}).map(([provider, count]) => (
                        <Detail key={provider} label={provider.replaceAll("_", " ")} value={count} />
                      ))}
                  </div>
                </Card>
              </div>
              {!!(discoveryHealth?.recent_errors || []).length && (
                <Card title="Recent Discovery Errors">
                  {(discoveryHealth.recent_errors || []).map(item => (
                    <div key={`${item.connection_id}-${item.last_sync_attempt_at || "error"}`} className="hmadmin-discovery-error">
                      <strong>{item.label} · {item.provider}</strong>
                      <span>{item.workspace_name || `Workspace ${item.workspace_id}`} · {fmtTime(item.last_sync_attempt_at)}</span>
                      <p>{item.error}</p>
                    </div>
                  ))}
                </Card>
              )}
              <Card title={`Discovery Sources — ${(discoveryHealth?.sources || []).length}`}>
                {(discoveryHealth?.sources || []).length === 0
                  ? <div className="hmadmin-empty">No discovery source health loaded.</div>
                  : <Table headers={["Source", "Workspace", "Permission", "Sync", "Checked", "Actions"]}>
                      {(discoveryHealth.sources || []).map(source => {
                        const syncType = source.last_sync_status === "error" ? "cancelled" : source.last_sync_status === "success" ? "active" : source.last_sync_status === "skipped" ? "trial" : "free";
                        return (
                          <tr key={source.id}>
                            <td><div className="name">{source.label}</div><div className="muted">{source.provider} · {source.source_type} · {source.source_account || "no account"}</div></td>
                            <td><div>{source.workspace_name || `Workspace ${source.workspace_id}`}</div><div className="email">{source.owner_email || "unknown owner"}</div></td>
                            <td><Badge type={source.permission_status === "connected" ? "active" : source.permission_status === "disabled_by_user" ? "cancelled" : "free"}>{source.permission_status}</Badge></td>
                            <td><Badge type={syncType}>{source.last_sync_status || "never"}</Badge><div className="muted">{fmtTime(source.last_sync_attempt_at)}</div>{source.last_sync_error && <div className="hmadmin-discovery-error-text">{source.last_sync_error}</div>}</td>
                            <td className="mono">{source.checked_item_count || 0}<div className="muted">{source.latest_item_id || "none"}</div></td>
                            <td><div className="actions"><button className="hmadmin-btn ghost xs" disabled={!!busy || source.permission_status !== "connected"} onClick={() => run(`discovery-${source.id}`, "Running source sync...", "Source sync complete", () => adminApi.runDiscoverySync({ connection_id: source.id }), false).then(refreshDiscoveryHealth)}>Run source</button></div></td>
                          </tr>
                        );
                      })}
                    </Table>}
              </Card>
            </>
          )}

          {/* ── FEATURE FLAGS ── */}
          {panel === "feature-flags" && (
            <div className="hmadmin-grid2">
              <Card title="Feature Flags & Config" action={<button className="hmadmin-btn primary xs" onClick={() => openModal("feature-flag")}>+ Set Flag</button>}>
                {featureFlags === null
                  ? <div className="hmadmin-empty">Loading feature flags...</div>
                  : featureFlags.length === 0
                    ? <div className="hmadmin-empty">No feature flags configured. Use "+ Set Flag" to add one.</div>
                    : <Table headers={["Key", "Value", "Updated", "Actions"]}>
                        {featureFlags.map(flag => (
                          <tr key={flag.key}>
                            <td className="mono">{flag.key}</td>
                            <td className="mono">{String(flag.value)}</td>
                            <td className="mono">{fmtDate(flag.updated_at, true)}</td>
                            <td><button className="hmadmin-btn ghost xs" onClick={() => { setForm(f => ({ ...f, flag_key: flag.key, flag_value: String(flag.value) })); setModal("feature-flag"); }}>Edit</button></td>
                          </tr>
                        ))}
                      </Table>
                }
              </Card>
              <Card title="Platform Limits Reference">
                <div className="hmadmin-action-list">
                  <Detail label="Free: AI requests/month" value="50 (backend constant)" />
                  <Detail label="Pro: AI requests/month" value="300 (backend constant)" />
                  <Detail label="Family: shared pool" value="500 (backend constant)" />
                  <Detail label="Trial length" value="14 days" />
                  <Detail label="Intelligence cron" value="Render nightly job" />
                </div>
                <div style={{ padding: "0 20px 16px", fontSize: 12, color: "var(--hmadmin-text3)" }}>
                  Hard limits live in backend env config. Feature flags here add dynamic per-workspace overrides.
                </div>
              </Card>
            </div>
          )}

          {/* ── AUDIT LOGS ── */}
          {panel === "logs" && (
            <Card title="Audit Log" action={<button className="hmadmin-btn ghost xs" onClick={() => exportData("billing")}>Export</button>}>
              <Table headers={["Timestamp", "Admin", "Action", "Target", "Result"]}>
                {logs.map(log => (
                  <tr key={log.id}>
                    <td className="mono">{fmtTime(log.created_at)}</td>
                    <td className="mono teal">#{log.admin_user_id}</td>
                    <td>{log.action}</td>
                    <td>{log.entity_type} {log.entity_id || ""}</td>
                    <td><Badge type="active">success</Badge></td>
                  </tr>
                ))}
              </Table>
              {!logs.length && <div className="hmadmin-empty">No audit log entries loaded.</div>}
            </Card>
          )}

          {/* ── SYSTEM HEALTH ── */}
          {panel === "system-health" && (
            <>
              <div className="hmadmin-kpi-grid">
                <Kpi label="API Status" value={health?.status || "unknown"} sub="backend health endpoint" tone={health?.status === "ok" ? "green" : "red"} />
                <Kpi label="DB Pool Checked Out" value={health?.db_pool_checked_out ?? "—"} sub={`of ${health?.db_pool_size || "?"} pool size`} tone="teal" />
                <Kpi label="Household Events" value={(health?.household_events || 0).toLocaleString()} sub="intelligence events queued" tone="purple" />
                <Kpi label="Pending Insights" value={health?.pending_insights || 0} sub="queued for next run" tone="amber" />
              </div>
              <div className="hmadmin-grid2">
                <Card title="Service Status">
                  <div className="hmadmin-action-list">
                    <ServiceRow label="Backend API" status={health?.status === "ok" ? "operational" : "degraded"} />
                    <ServiceRow label="Database" status={health?.db_connected !== false ? "operational" : "error"} />
                    <ServiceRow label="Anthropic AI" status={health?.anthropic_status || "unknown"} />
                    <ServiceRow label="OpenAI" status={health?.openai_status || "unknown"} />
                    <ServiceRow label="Stripe Webhooks" status={health?.stripe_webhook_ok ? "operational" : "unknown"} />
                    <ServiceRow label="Email Delivery" status={health?.email_delivery_status || "unknown"} />
                    <ServiceRow label="Intelligence Engine" status={health?.intelligence_last_run ? `Last: ${fmtDate(health.intelligence_last_run, true)}` : "never run"} />
                    <ServiceRow label="Reminder Email Job" status={health?.reminders_last_run ? `Last: ${fmtDate(health.reminders_last_run, true)}` : "never run"} />
                    <ServiceRow label="CPSC Recall Check" status={health?.recall_last_run ? `Last: ${fmtDate(health.recall_last_run, true)}` : "never run"} />
                  </div>
                </Card>
                <Card title="Raw Health Payload">
                  <pre style={{ padding: 20, fontSize: 11, fontFamily: "monospace", color: "var(--hmadmin-text3)", overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                    {JSON.stringify(health, null, 2)}
                  </pre>
                </Card>
              </div>
            </>
          )}

          {/* ── COMPLIANCE ── */}
          {panel === "compliance" && (
            <div className="hmadmin-grid-6040">
              <Card title="GDPR / CCPA Request Queue" action={<button className="hmadmin-btn ghost xs" onClick={() => setGdprQueue(null)}>Refresh</button>}>
                {gdprQueue === null
                  ? <div className="hmadmin-empty">Loading compliance queue...</div>
                  : gdprQueue.length === 0
                    ? <div className="hmadmin-empty">No pending requests. Requests appear here when users submit data deletion or export requests.</div>
                    : <Table headers={["User", "Request Type", "Submitted", "Status", "Actions"]}>
                        {gdprQueue.map(req => (
                          <tr key={req.id}>
                            <td><div className="name">{req.email}</div></td>
                            <td><Badge type={req.request_type === "deletion" ? "cancelled" : "trial"}>{req.request_type}</Badge></td>
                            <td className="mono">{fmtDate(req.created_at, true)}</td>
                            <td><Badge type={req.status === "completed" ? "active" : "trial"}>{req.status}</Badge></td>
                            <td><div className="actions">
                              {req.request_type === "export" && <button className="hmadmin-btn primary xs" onClick={() => openModal("gdpr-export", users.find(u => u.email === req.email))}>Generate</button>}
                              {req.request_type === "deletion" && <button className="hmadmin-btn danger xs" onClick={() => openModal("delete", users.find(u => u.email === req.email))}>Delete</button>}
                              <button className="hmadmin-btn ghost xs" onClick={() => withConfirm({ title: "Mark GDPR request as completed?", message: `This marks the ${req.request_type} request from ${req.email} as completed. Ensure the actual data action (export or deletion) was performed before marking done.`, label: "Mark completed", onConfirm: () => run("gdpr-process", "Processing...", "Request processed", () => adminApi.gdprProcess(req.id, { status: "completed" }), false) })}>Mark done</button>
                            </div></td>
                          </tr>
                        ))}
                      </Table>
                }
              </Card>
              <Card title="Compliance Actions">
                <div className="hmadmin-action-list">
                  <Action title="Manual data export" text="Generate full data export for a user on request." action="Export user" onClick={() => openModal("gdpr-export", selectedUser)} />
                  <Action title="Delete user data" text="Permanent deletion — GDPR Article 17 erasure." action="Delete" danger onClick={() => openModal("delete", selectedUser)} />
                  <Action title="Export all users (CCPA)" text="Download full user registry for compliance." action="Export all" onClick={() => exportData("users")} />
                </div>
                <div className="hmadmin-action-list">
                  <Detail label="Data retention" value="Until account deletion" />
                  <Detail label="Right to erasure" value="Admin delete → full purge" />
                  <Detail label="Data portability" value="JSON export via admin" />
                </div>
              </Card>
            </div>
          )}

          {/* ── SECURITY ── */}
          {panel === "security" && (
            <div className="hmadmin-grid2">
              <Card title="Admin Access">
                <div className="hmadmin-action-list">
                  <Detail label="Admin guard" value="ADMIN_EMAILS env + JWT" />
                  <Detail label="Current admin" value={user?.email || "unknown"} />
                  <Detail label="Admin route" value="/app/admin (RequireAdmin)" />
                  <Detail label="2FA" value="Not implemented — recommended" />
                  <Detail label="Session" value="JWT expiry per backend config" />
                </div>
              </Card>
              <Card
                title="Recent Security Events"
                action={<button className="hmadmin-btn ghost xs" onClick={() => adminApi.securityEvents(100).then(r => setSecurityEvents(r)).catch(() => setSecurityEvents([]))}>Refresh</button>}
              >
                <Table headers={["Time", "Severity", "Event", "Actor", "Endpoint", "Reason"]}>
                  {(securityEvents || []).slice(0, 25).map(event => (
                    <tr key={event.id}>
                      <td className="mono">{fmtTime(event.created_at)}</td>
                      <td><Badge type={event.severity === "high" || event.severity === "medium" ? "cancelled" : "active"}>{event.severity}</Badge></td>
                      <td>{event.event_type}</td>
                      <td>
                        <div className="mono teal">{event.email || "unknown"}</div>
                        <div className="email">{event.ip_address || "unknown IP"}</div>
                      </td>
                      <td className="mono">{event.method || ""} {event.endpoint || ""} {event.status_code ? `(${event.status_code})` : ""}</td>
                      <td>{event.reason || "No reason recorded"}</td>
                    </tr>
                  ))}
                </Table>
                {securityEvents === null && <div className="hmadmin-empty">Loading security events...</div>}
                {securityEvents?.length === 0 && <div className="hmadmin-empty">No security events recorded yet.</div>}
              </Card>
              <Card title="Suspicious Activity Signals">
                <div className="hmadmin-action-list">
                  {users.filter(u => (u.ai_requests_used||0) > (u.ai_requests_limit||999)).length > 0 && <Detail label="Users over AI limit" value={String(users.filter(u=>(u.ai_requests_used||0)>(u.ai_requests_limit||999)).length)} />}
                  {failedPayments.length > 5 && <Detail label="High failed payment volume" value={`${failedPayments.length} failures`} />}
                  <div className="hmadmin-empty" style={{ marginTop: 8 }}>Automated anomaly detection requires external alerting (Sentry, PagerDuty).</div>
                </div>
              </Card>
              <Card title="Admin Role Management">
                <div className="hmadmin-action-list">
                  <Action title="Add admin account" text="Add email to ADMIN_EMAILS env var and redeploy." action="View docs" onClick={() => window.open("https://render.com/docs/environment-variables","_blank")} />
                  <Action title="Revoke admin access" text="Remove email from ADMIN_EMAILS and redeploy." action="Instructions" onClick={() => setStatus("Remove email from ADMIN_EMAILS env var, then redeploy to revoke access.")} />
                </div>
              </Card>
            </div>
          )}

          {/* ── SETTINGS ── */}
          {panel === "settings" && (
            <div className="hmadmin-grid2">
              <Card title="System Configuration">
                <div style={{ padding: 20 }}>
                  <div className="hmadmin-form-group"><label>Default AI Provider</label><input value="backend configured via env" readOnly /></div>
                  <div className="hmadmin-form-group"><label>Intelligence Cron</label><input value="Render nightly job" readOnly /></div>
                  <div className="hmadmin-form-group"><label>Admin Route</label><input value="/app/admin (RequireAdmin guard)" readOnly /></div>
                </div>
              </Card>
              <Card title="Admin Account">
                <div style={{ padding: 20 }}>
                  <div className="hmadmin-form-group"><label>Admin Email</label><input value={user?.email || ""} readOnly /></div>
                  <div className="hmadmin-form-group"><label>Backend Guard</label><input value="ADMIN_EMAILS + JWT" readOnly /></div>
                </div>
              </Card>
              <Card title="Danger Zone">
                <div style={{ padding: 20 }}>
                  <div style={{ background: "#FF475722", border: "1px solid var(--hmadmin-red)", borderRadius: 8, padding: "12px 14px" }}>
                    <div style={{ fontWeight: 700, color: "var(--hmadmin-red)", marginBottom: 4 }}>Clear system notice</div>
                    <div style={{ fontSize: 12, color: "var(--hmadmin-text2)", marginBottom: 8 }}>Remove any active in-app system announcement.</div>
                    <button className="hmadmin-btn danger xs" disabled={!notice} onClick={() => withConfirm({ title: "Clear system notice?", message: notice ? `"${notice.message}" will be removed from the app immediately for all users.` : "No active notice.", danger: true, label: "Clear notice", onConfirm: () => run("clear-notice","Clearing...","Notice cleared",async()=>{await adminApi.clearSystemNotice();setNotice(null);},false) })}>Clear notice</button>
                  </div>
                </div>
              </Card>
            </div>
          )}

        </div>
      </main>

      <AdminModal modal={modal} form={form} setForm={setForm} submit={submitModal} close={() => setModal(null)} busy={busy} />
      {confirmAction && (
        <div className="hmadmin-modal-overlay" onMouseDown={e => e.target === e.currentTarget && setConfirmAction(null)}>
          <div className="hmadmin-modal" style={{ maxWidth: 440 }}>
            <div className="hmadmin-modal-header">
              <div className={confirmAction.danger ? "is-danger" : ""}>{confirmAction.title}</div>
              <button type="button" onClick={() => setConfirmAction(null)}>×</button>
            </div>
            <div className="hmadmin-modal-body">
              <p style={{ fontSize: 14, color: "var(--hmadmin-text2)", lineHeight: 1.65 }}>{confirmAction.message}</p>
              {confirmAction.danger && (
                <div style={{ marginTop: 12, padding: "10px 12px", background: "#FF475722", border: "1px solid var(--hmadmin-red)", borderRadius: 6, fontSize: 12, color: "var(--hmadmin-red)" }}>
                  ⚠ This action cannot be undone.
                </div>
              )}
            </div>
            <div className="hmadmin-modal-footer">
              <button className="hmadmin-btn ghost" onClick={() => setConfirmAction(null)}>Cancel</button>
              <button
                className={`hmadmin-btn ${confirmAction.danger ? "danger" : "primary"}`}
                disabled={!!busy}
                onClick={() => { confirmAction.onConfirm(); setConfirmAction(null); }}
              >
                {confirmAction.label || "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Table({ headers, children }) {
  return (
    <div className="hmadmin-table-wrap">
      <table><thead><tr>{headers.map(h => <th key={h}>{h}</th>)}</tr></thead><tbody>{children}</tbody></table>
    </div>
  );
}

function UserRow({ user, onSelect, onModal, full }) {
  const counts = user.module_counts || {};
  return (
    <tr>
      <td><button className="hmadmin-link-name" type="button" onClick={onSelect}>{user.full_name}</button><div className="email">{user.email}</div></td>
      <td><Badge type={user.personal_plan_type === "pro" ? "pro" : "free"}>{user.personal_plan_type === "pro" ? "Pro" : "Free"}</Badge></td>
      {full && <td><Badge type={user.access_source === "trial" ? "trial" : "active"}>{user.access_source}</Badge></td>}
      <td>{full ? `Food ${counts.pantry_items||0} · Vault ${counts.records||0} · Vehicles ${counts.vehicles||0}` : <span className="mono">{user.ai_requests_used||0}/{user.ai_requests_limit||0}</span>}</td>
      <td className="mono">{full ? `${user.ai_requests_used||0}/${user.ai_requests_limit||0}` : fmtDate(user.created_at, true)}</td>
      <td><div className="actions"><button className="hmadmin-btn ghost xs" onClick={onSelect}>View</button><button className="hmadmin-btn primary xs" onClick={() => onModal("plan", user)}>Plan</button><button className="hmadmin-btn blue xs" onClick={() => onModal("email", user)}>Email</button></div></td>
    </tr>
  );
}

function ServiceRow({ label, status }) {
  const isOk = status === "operational";
  const isUnknown = status === "unknown";
  return (
    <div className="hmadmin-action-row">
      <div><strong>{label}</strong><p style={{ color: isOk ? "var(--hmadmin-green)" : isUnknown ? "var(--hmadmin-text3)" : "var(--hmadmin-amber)" }}>{status}</p></div>
      <div style={{ width: 8, height: 8, borderRadius: "50%", background: isOk ? "var(--hmadmin-green)" : isUnknown ? "var(--hmadmin-text3)" : "var(--hmadmin-amber)", boxShadow: isOk ? "0 0 6px var(--hmadmin-green)" : "none" }} />
    </div>
  );
}

function MiniStat({ label, value }) {
  return <div className="hmadmin-stat-item"><div>{value}</div><span>{label}</span></div>;
}

function Action({ title, text, action, onClick, danger }) {
  return (
    <div className="hmadmin-action-row">
      <div><strong>{title}</strong><p>{text}</p></div>
      <button className={`hmadmin-btn ${danger ? "danger" : "ghost"} xs`} onClick={onClick}>{action}</button>
    </div>
  );
}

function Detail({ label, value }) {
  return <div className="hmadmin-detail"><span>{label}</span><strong className="mono" style={{ fontSize: 12 }}>{value}</strong></div>;
}

function Progress({ value }) {
  return <div className="hmadmin-progress"><span style={{ width: `${Math.min(100, Math.max(0, value))}%` }} /><em>{value}%</em></div>;
}

function InsightList({ insights }) {
  return <Card title="Priority Intelligence"><InsightRows insights={insights} compact /></Card>;
}

function InsightRows({ insights, compact }) {
  if (!insights.length) return <div className="hmadmin-empty">No insights yet.</div>;
  return (
    <div>
      {insights.map(item => (
        <div className="hmadmin-insight" key={item.id}>
          <i className={`p-${item.priority}`} />
          <div><strong>{item.title}</strong><p>{item.message}</p><div>{(item.modules||[]).map(mod => <span key={mod}>{mod}</span>)}</div></div>
          {!compact && item.savings_amount && <em>{money(item.savings_amount)}</em>}
        </div>
      ))}
    </div>
  );
}

function Timeline({ logs }) {
  return (
    <Card title="Activity Timeline">
      <div className="hmadmin-timeline">
        {logs.length ? logs.map((log, idx) => (
          <div className="hmadmin-tl-item" key={`${log.id}-${idx}`}>
            <i /><div><strong>{log.action||log.event_type}</strong><p>{log.message||`${log.entity_type||"system"} ${log.entity_id||""}`}</p><span>{fmtTime(log.created_at)}</span></div>
          </div>
        )) : <div className="hmadmin-empty">No activity yet.</div>}
      </div>
    </Card>
  );
}

function ModuleHealth({ name, count, detail, tone, total }) {
  const pct = Math.min(100, Math.round(((count||0) / Math.max(total, 1)) * 100));
  return (
    <div className="hmadmin-module-health">
      <div><strong>{name}</strong><em>{count||0}</em></div>
      <p>{detail}</p>
      <span><i style={{ width: `${pct}%`, background: `var(--hmadmin-${tone})` }} /></span>
    </div>
  );
}

function AdminModal({ modal, form, setForm, submit, close, busy }) {
  const titles = {
    plan: "Change Plan", trial: "Extend Trial / Grant Access", cancel: "Cancel Subscription",
    pause: "Pause Subscription", delete: "Permanently Delete Account", email: "Send Email to User",
    broadcast: "Email Broadcast", notice: "System Notice", flag: "Flag User",
    "add-note": "Add CRM Note", refund: "Record Refund", "feature-flag": "Set Feature Flag",
    "gdpr-export": "Trigger GDPR Data Export", impersonate: "Login As User",
    "bulk-email": "Bulk Email", "bulk-plan": "Bulk Plan Change",
  };
  const dangerModals = ["delete", "cancel", "impersonate"];
  const title = titles[modal];
  if (!title) return null;
  return (
    <Modal title={title} tone={dangerModals.includes(modal) ? "danger" : ""} onClose={close}
      footer={<><button className="hmadmin-btn ghost" onClick={close}>Cancel</button><button className={`hmadmin-btn ${dangerModals.includes(modal) ? "danger" : "primary"}`} disabled={!!busy} onClick={submit}>{modal === "delete" ? "Delete forever" : "Confirm"}</button></>}>
      {modal === "plan" && <>
        <Field label="New Plan"><select value={form.plan||"none"} onChange={e => setForm(f => ({...f, plan: e.target.value}))}><option value="none">Free</option><option value="pro">Pro</option></select></Field>
        <Field label="Access Source"><select value={form.source||"manual"} onChange={e => setForm(f => ({...f, source: e.target.value}))}>{ACCESS_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}</select></Field>
        <Field label="Expiry Date"><input type="date" value={form.expiry||""} onChange={e => setForm(f => ({...f, expiry: e.target.value}))} /></Field>
      </>}
      {modal === "trial" && <>
        <Field label="Days to Add"><input type="number" min="1" max="365" value={form.days||14} onChange={e => setForm(f => ({...f, days: e.target.value}))} /></Field>
        <Field label="Reason"><input value={form.reason||""} onChange={e => setForm(f => ({...f, reason: e.target.value}))} /></Field>
      </>}
      {modal === "cancel" && <>
        <div className="hmadmin-danger-note">The subscription will be marked cancelled. Access is retained until end of billing period.</div>
        <Field label="Cancellation Reason"><textarea value={form.reason||""} onChange={e => setForm(f => ({...f, reason: e.target.value}))} /></Field>
      </>}
      {modal === "pause" && <>
        <Field label="Pause Duration"><select value={form.months||1} onChange={e => setForm(f => ({...f, months: e.target.value}))}><option value="1">1 month</option><option value="2">2 months</option><option value="3">3 months</option></select></Field>
      </>}
      {modal === "delete" && <>
        <div className="hmadmin-danger-note"><strong style={{ color: "var(--hmadmin-red)" }}>This cannot be undone.</strong> All workspace data will be permanently deleted.</div>
        <Field label={`Type "${form.email}" to confirm`}><input value={form.confirm||""} onChange={e => setForm(f => ({...f, confirm: e.target.value}))} /></Field>
      </>}
      {(modal === "email" || modal === "bulk-email") && <>
        <Field label="Subject"><input value={form.subject||""} onChange={e => setForm(f => ({...f, subject: e.target.value}))} /></Field>
        <Field label="Message"><textarea value={form.body||""} onChange={e => setForm(f => ({...f, body: e.target.value}))} /></Field>
      </>}
      {modal === "broadcast" && <>
        <Field label="Audience"><select value={form.audience||"all"} onChange={e => setForm(f => ({...f, audience: e.target.value}))}><option value="all">All users</option><option value="free">Free only</option><option value="trial">Trial only</option><option value="pro">Pro only</option><option value="family">Family only</option><option value="churned">Cancelled / at-risk</option></select></Field>
        <Field label="Subject"><input value={form.subject||""} onChange={e => setForm(f => ({...f, subject: e.target.value}))} /></Field>
        <Field label="Message"><textarea value={form.body||""} onChange={e => setForm(f => ({...f, body: e.target.value}))} /></Field>
      </>}
      {modal === "notice" && <>
        <Field label="Notice Type"><select value={form.notice_type||"info"} onChange={e => setForm(f => ({...f, notice_type: e.target.value}))}><option value="info">Info</option><option value="warning">Warning</option><option value="maintenance">Maintenance</option></select></Field>
        <Field label="Message"><input value={form.message||""} onChange={e => setForm(f => ({...f, message: e.target.value}))} /></Field>
      </>}
      {modal === "flag" && <>
        <Field label="Flag Type"><select value={form.flag_type||"review"} onChange={e => setForm(f => ({...f, flag_type: e.target.value}))}><option value="review">Needs Review</option><option value="fraud">Fraud Suspected</option><option value="vip">VIP</option><option value="churn_risk">Churn Risk</option><option value="abuse">ToS Abuse</option></select></Field>
        <Field label="Internal Note"><textarea value={form.note||""} onChange={e => setForm(f => ({...f, note: e.target.value}))} /></Field>
      </>}
      {modal === "add-note" && <>
        <Field label="Internal CRM Note"><textarea value={form.note||""} onChange={e => setForm(f => ({...f, note: e.target.value}))} placeholder="Notes are only visible in the admin panel." /></Field>
      </>}
      {modal === "refund" && <>
        <div className="hmadmin-danger-note" style={{ border: "1px solid var(--hmadmin-amber)", background: "var(--hmadmin-amber)22", color: "var(--hmadmin-amber)" }}>Records refund in audit log. Process the actual charge refund in Stripe dashboard.</div>
        <Field label="Refund Amount (USD)"><input type="number" min="0" step="0.01" value={form.refund_amount||""} onChange={e => setForm(f => ({...f, refund_amount: e.target.value}))} placeholder="9.00" /></Field>
        <Field label="Reason"><textarea value={form.refund_reason||""} onChange={e => setForm(f => ({...f, refund_reason: e.target.value}))} /></Field>
      </>}
      {modal === "feature-flag" && <>
        <Field label="Flag Key"><input value={form.flag_key||""} onChange={e => setForm(f => ({...f, flag_key: e.target.value}))} placeholder="e.g. free_ai_limit" /></Field>
        <Field label="Value"><input value={form.flag_value||""} onChange={e => setForm(f => ({...f, flag_value: e.target.value}))} placeholder="e.g. 50" /></Field>
      </>}
      {modal === "gdpr-export" && <p style={{ fontSize: 13, color: "var(--hmadmin-text2)" }}>This will generate a full data export for <strong>{form.email}</strong> and deliver it via the backend.</p>}
      {modal === "impersonate" && <>
        <div className="hmadmin-danger-note">Impersonation is fully audited. A short-lived user session will open in a new tab. The token is also printed to the console as a fallback.</div>
        <p style={{ fontSize: 13, color: "var(--hmadmin-text2)", marginTop: 12 }}>Impersonating: <strong>{form.email}</strong></p>
      </>}
    </Modal>
  );
}

function Field({ label, children }) {
  return <label className="hmadmin-form-group"><span>{label}</span>{children}</label>;
}

function Style() {
  return <><style>{`
.hm-main:has(.hmadmin-wrap){padding:0;display:block;background:#0A0B0D}.hmadmin-wrap,.hmadmin-wrap *{box-sizing:border-box}.hmadmin-wrap{--hmadmin-bg:#0A0B0D;--hmadmin-surface:#111318;--hmadmin-surface2:#181B22;--hmadmin-surface3:#1E2229;--hmadmin-border:#252A34;--hmadmin-border2:#2E3542;--hmadmin-text:#E8EBF0;--hmadmin-text2:#9AA3B0;--hmadmin-text3:#5C6470;--hmadmin-teal:#00D4AA;--hmadmin-amber:#F5A623;--hmadmin-red:#FF4757;--hmadmin-blue:#4A9EFF;--hmadmin-purple:#A855F7;--hmadmin-green:#22C55E;min-height:100vh;background:var(--hmadmin-bg);color:var(--hmadmin-text);display:flex;font-family:'DM Sans',system-ui,sans-serif}.hmadmin-sidebar{width:240px;flex-shrink:0;background:var(--hmadmin-surface);border-right:1px solid var(--hmadmin-border);height:100vh;position:sticky;top:0;display:flex;flex-direction:column;overflow:auto}.hmadmin-sb-header{padding:20px;border-bottom:1px solid var(--hmadmin-border)}.hmadmin-logo{font-size:16px;font-weight:800;letter-spacing:-.5px;display:flex;gap:8px;align-items:center}.hmadmin-logo span{width:8px;height:8px;border-radius:50%;background:var(--hmadmin-teal);box-shadow:0 0 8px var(--hmadmin-teal)}.hmadmin-sb-badge{font-family:monospace;font-size:9px;background:#00D4AA22;color:var(--hmadmin-teal);padding:2px 7px;border-radius:4px;margin-top:6px;display:inline-block;letter-spacing:.08em}.hmadmin-admin-info{margin-top:12px;padding:10px 12px;background:var(--hmadmin-surface2);border:1px solid var(--hmadmin-border);border-radius:8px}.hmadmin-admin-info div{font-family:monospace;font-size:11px;color:var(--hmadmin-teal)}.hmadmin-admin-info small{font-size:10px;color:var(--hmadmin-text3);text-transform:uppercase;letter-spacing:.1em}.hmadmin-sb-nav{padding:12px 0;flex:1}.hmadmin-sb-section{padding:12px 20px 4px;font-family:monospace;font-size:9px;font-weight:700;color:var(--hmadmin-text3);text-transform:uppercase;letter-spacing:.12em}.hmadmin-sb-item{width:100%;border:0;background:transparent;display:flex;align-items:center;gap:10px;padding:8px 20px;color:var(--hmadmin-text2);font-size:13px;font-weight:600;cursor:pointer;border-left:2px solid transparent;text-align:left}.hmadmin-sb-item:hover{background:var(--hmadmin-surface2);color:var(--hmadmin-text)}.hmadmin-sb-item.active{color:var(--hmadmin-teal);background:#00D4AA22;border-left-color:var(--hmadmin-teal)}.hmadmin-sb-item span{width:18px;font-family:monospace;text-align:center}.hmadmin-sb-item em{margin-left:auto;font-style:normal;font-family:monospace;font-size:10px;background:#FF475722;color:var(--hmadmin-red);padding:1px 6px;border-radius:4px}.hmadmin-sb-footer{padding:16px 20px;border-top:1px solid var(--hmadmin-border);font-family:monospace;font-size:10px;color:var(--hmadmin-text3)}.hmadmin-main{flex:1;min-width:0}.hmadmin-topbar{height:56px;position:sticky;top:0;z-index:5;background:rgba(10,11,13,.92);backdrop-filter:blur(12px);border-bottom:1px solid var(--hmadmin-border);padding:0 32px;display:flex;align-items:center;justify-content:space-between}.hmadmin-topbar strong{font-size:15px}.hmadmin-topbar div{display:flex;gap:16px;align-items:center;font-family:monospace;font-size:11px;color:var(--hmadmin-text3)}.hmadmin-live i{display:inline-block;width:6px;height:6px;border-radius:50%;background:var(--hmadmin-green);box-shadow:0 0 6px var(--hmadmin-green);margin-right:6px}.hmadmin-content{padding:32px}.hmadmin-alert{border:1px solid var(--hmadmin-red);background:#FF475722;color:var(--hmadmin-red);border-radius:8px;padding:12px 14px;margin-bottom:16px}.hmadmin-alert.success{border-color:var(--hmadmin-green);background:#22C55E22;color:var(--hmadmin-green)}.hmadmin-kpi-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:16px;margin-bottom:24px}.hmadmin-kpi{background:var(--hmadmin-surface);border:1px solid var(--hmadmin-border);border-radius:12px;padding:20px;position:relative;overflow:hidden}.hmadmin-kpi:before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:var(--kpi-color,var(--hmadmin-teal))}.hmadmin-kpi-label{font-family:monospace;font-size:11px;color:var(--hmadmin-text3);text-transform:uppercase;letter-spacing:.1em;margin-bottom:10px}.hmadmin-kpi-value{font-size:32px;font-weight:800;letter-spacing:-1px;line-height:1;margin-bottom:8px}.hmadmin-kpi-sub,.hmadmin-muted{font-size:12px;color:var(--hmadmin-text3)}.hmadmin-grid2{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:24px}.hmadmin-grid-6040{display:grid;grid-template-columns:1.5fr 1fr;gap:16px;margin-bottom:24px}.hmadmin-card{background:var(--hmadmin-surface);border:1px solid var(--hmadmin-border);border-radius:12px;overflow:hidden;margin-bottom:16px}.hmadmin-card-header{padding:16px 20px;border-bottom:1px solid var(--hmadmin-border);display:flex;align-items:center;justify-content:space-between;gap:12px}.hmadmin-card-title{font-weight:800;font-size:13px}.hmadmin-card-body{padding:0}.hmadmin-table-wrap{overflow:auto}table{width:100%;border-collapse:collapse}th{padding:10px 20px;font-family:monospace;font-size:10px;color:var(--hmadmin-text3);text-transform:uppercase;letter-spacing:.1em;text-align:left;background:var(--hmadmin-surface2);border-bottom:1px solid var(--hmadmin-border)}td{padding:12px 20px;font-size:13px;color:var(--hmadmin-text2);border-bottom:1px solid var(--hmadmin-border);vertical-align:middle}tr:last-child td{border-bottom:none}tr:hover td{background:var(--hmadmin-surface2);color:var(--hmadmin-text)}.name,.hmadmin-link-name{color:var(--hmadmin-text);font-weight:700}.hmadmin-link-name{border:0;background:transparent;cursor:pointer;padding:0}.email,.muted{font-family:monospace;font-size:11px;color:var(--hmadmin-text3)}.mono{font-family:monospace}.teal{color:var(--hmadmin-teal)}.hmadmin-badge{display:inline-flex;padding:2px 8px;border-radius:4px;font-family:monospace;font-size:10px;font-weight:800;text-transform:uppercase;white-space:nowrap}.hmadmin-badge-pro{background:#00D4AA22;color:var(--hmadmin-teal)}.hmadmin-badge-free{background:var(--hmadmin-surface3);color:var(--hmadmin-text3)}.hmadmin-badge-family{background:#A855F722;color:var(--hmadmin-purple)}.hmadmin-badge-trial{background:#F5A62322;color:var(--hmadmin-amber)}.hmadmin-badge-active{background:#22C55E22;color:var(--hmadmin-green)}.hmadmin-badge-cancelled{background:#FF475722;color:var(--hmadmin-red)}.hmadmin-badge-admin{background:#4A9EFF22;color:var(--hmadmin-blue)}.hmadmin-btn{padding:8px 14px;border-radius:6px;border:1px solid transparent;font-size:12px;font-weight:800;cursor:pointer;background:var(--hmadmin-surface2);color:var(--hmadmin-text2)}.hmadmin-btn.xs{padding:4px 10px;font-size:11px}.hmadmin-btn.primary{background:var(--hmadmin-teal);color:#000}.hmadmin-btn.ghost{border-color:var(--hmadmin-border);background:var(--hmadmin-surface2)}.hmadmin-btn.blue{border-color:var(--hmadmin-blue);background:#4A9EFF22;color:var(--hmadmin-blue)}.hmadmin-btn.danger{border-color:var(--hmadmin-red);background:#FF475722;color:var(--hmadmin-red)}.actions{display:flex;gap:6px;align-items:center;flex-wrap:wrap}.hmadmin-action-list{padding:12px 20px}.hmadmin-action-row{display:flex;align-items:center;justify-content:space-between;gap:16px;padding:12px 0;border-bottom:1px solid var(--hmadmin-border)}.hmadmin-action-row:last-child{border-bottom:0}.hmadmin-action-row p{margin:4px 0 0;color:var(--hmadmin-text3);font-size:12px}.hmadmin-insight{padding:14px 20px;border-bottom:1px solid var(--hmadmin-border);display:flex;gap:12px}.hmadmin-insight i{width:4px;align-self:stretch;border-radius:2px;background:var(--hmadmin-amber)}.hmadmin-insight i.p-urgent,.hmadmin-insight i.p-high{background:var(--hmadmin-red)}.hmadmin-insight i.p-savings{background:var(--hmadmin-green)}.hmadmin-insight p{font-size:12px;color:var(--hmadmin-text3);line-height:1.5;margin:3px 0}.hmadmin-insight span{font-family:monospace;font-size:9px;background:var(--hmadmin-surface3);color:var(--hmadmin-text3);padding:1px 6px;border-radius:3px;margin-right:4px}.hmadmin-insight em{margin-left:auto;color:var(--hmadmin-green);font-family:monospace;font-style:normal}.hmadmin-timeline{padding:16px 20px}.hmadmin-tl-item{display:flex;gap:12px;padding:10px 0;border-bottom:1px solid var(--hmadmin-border)}.hmadmin-tl-item i{width:8px;height:8px;border-radius:50%;background:var(--hmadmin-teal);margin-top:4px}.hmadmin-tl-item p{font-size:11px;color:var(--hmadmin-text3);margin:2px 0}.hmadmin-tl-item span{font-family:monospace;font-size:10px;color:var(--hmadmin-text3)}.hmadmin-user-hero{background:var(--hmadmin-surface);border:1px solid var(--hmadmin-border);border-radius:12px;padding:24px;margin-bottom:16px;display:flex;gap:20px;align-items:flex-start}.hmadmin-avatar{width:56px;height:56px;border-radius:12px;background:#00D4AA22;border:2px solid var(--hmadmin-teal);display:grid;place-items:center;font-size:22px;font-weight:900;color:var(--hmadmin-teal)}.hmadmin-user-hero h2{margin:0 0 4px;font-size:22px}.hmadmin-user-email{font-family:monospace;font-size:12px;color:var(--hmadmin-teal);margin-bottom:10px}.hmadmin-tags{display:flex;gap:6px;flex-wrap:wrap}.hmadmin-user-actions{margin-left:auto;display:flex;flex-direction:column;gap:8px}.hmadmin-stat-row{display:grid;grid-template-columns:repeat(auto-fit,minmax(100px,1fr));gap:12px;margin-bottom:16px}.hmadmin-stat-item{background:var(--hmadmin-surface);border:1px solid var(--hmadmin-border);border-radius:8px;padding:14px;text-align:center}.hmadmin-stat-item div{font-size:22px;font-weight:900}.hmadmin-stat-item span{font-family:monospace;font-size:10px;color:var(--hmadmin-text3)}.hmadmin-detail-list{padding:20px;display:grid;gap:10px}.hmadmin-detail{display:flex;justify-content:space-between;gap:12px;border-bottom:1px solid var(--hmadmin-border);padding-bottom:8px}.hmadmin-detail span{color:var(--hmadmin-text3);font-family:monospace;font-size:11px}.hmadmin-progress{display:flex;align-items:center;gap:8px}.hmadmin-progress>span{display:block;height:4px;width:70px;background:var(--hmadmin-green);border-radius:2px}.hmadmin-progress em{font-family:monospace;font-size:10px;color:var(--hmadmin-green);font-style:normal}.hmadmin-module-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:10px}.hmadmin-module-health{background:var(--hmadmin-surface);border:1px solid var(--hmadmin-border);border-radius:8px;padding:14px}.hmadmin-module-health div{display:flex;justify-content:space-between}.hmadmin-module-health em{font-style:normal;font-family:monospace;font-size:18px;font-weight:900}.hmadmin-module-health p{font-size:11px;color:var(--hmadmin-text3);margin:8px 0}.hmadmin-module-health>span{display:block;height:3px;background:var(--hmadmin-surface3);border-radius:2px}.hmadmin-module-health i{display:block;height:100%;border-radius:2px}.hmadmin-revenue-big{font-size:48px;font-weight:900;color:var(--hmadmin-teal);letter-spacing:-2px;padding:20px 20px 6px}.hmadmin-chart{height:200px;padding:20px;display:flex;align-items:flex-end;gap:8px}.hmadmin-chart span{flex:1;background:var(--hmadmin-teal);opacity:.7;border-radius:4px 4px 0 0}.hmadmin-search{padding:10px 14px;background:var(--hmadmin-surface);border:1px solid var(--hmadmin-border);border-radius:8px;color:var(--hmadmin-text);font-size:13px;outline:0}.hmadmin-empty{padding:20px;color:var(--hmadmin-text3);text-align:center}.hmadmin-notice{padding:20px}.hmadmin-notice p{color:var(--hmadmin-text2);margin:8px 0}.hmadmin-notice small{color:var(--hmadmin-text3);font-family:monospace}.hmadmin-modal-overlay{position:fixed;inset:0;z-index:50;background:rgba(0,0,0,.7);backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:24px}.hmadmin-modal{background:var(--hmadmin-surface);border:1px solid var(--hmadmin-border2);border-radius:16px;width:100%;max-width:560px;max-height:90vh;overflow:auto}.hmadmin-modal-header{padding:20px 24px;border-bottom:1px solid var(--hmadmin-border);display:flex;justify-content:space-between;font-weight:900}.hmadmin-modal-header .is-danger{color:var(--hmadmin-red)}.hmadmin-modal-header button{background:transparent;border:0;color:var(--hmadmin-text3);cursor:pointer}.hmadmin-modal-body{padding:24px}.hmadmin-modal-footer{padding:16px 24px;border-top:1px solid var(--hmadmin-border);display:flex;justify-content:flex-end;gap:10px}.hmadmin-form-group{display:block;margin-bottom:16px}.hmadmin-form-group span,.hmadmin-form-group label{display:block;font-family:monospace;font-size:11px;color:var(--hmadmin-text3);text-transform:uppercase;letter-spacing:.08em;margin-bottom:6px}.hmadmin-form-group input,.hmadmin-form-group select,.hmadmin-form-group textarea{width:100%;padding:10px 14px;background:var(--hmadmin-surface2);border:1px solid var(--hmadmin-border);border-radius:8px;color:var(--hmadmin-text);font-size:13px;outline:0}.hmadmin-form-group textarea{min-height:100px;resize:vertical}.hmadmin-danger-note{border:1px solid var(--hmadmin-red);background:#FF475722;color:var(--hmadmin-text2);border-radius:8px;padding:14px;margin-bottom:16px}.hmadmin-loading{min-height:60vh;display:grid;place-items:center;color:var(--hmadmin-text2)}@media(max-width:1000px){.hmadmin-sidebar{display:none}.hmadmin-kpi-grid,.hmadmin-grid2,.hmadmin-grid-6040,.hmadmin-module-grid{grid-template-columns:1fr}.hmadmin-content{padding:18px}.hmadmin-user-hero{flex-direction:column}.hmadmin-user-actions{margin-left:0;width:100%}}
`}</style><style>{`
.hm-main:has(.hmadmin-wrap){background:#F7F8FA}.hmadmin-wrap{--hmadmin-bg:#F7F8FA;--hmadmin-surface:#FFFFFF;--hmadmin-surface2:#F0F4F7;--hmadmin-surface3:#E5EBF0;--hmadmin-border:#D9E1E8;--hmadmin-border2:#C7D2DC;--hmadmin-text:#172033;--hmadmin-text2:#4B5565;--hmadmin-text3:#7C8797;--hmadmin-teal:#0EA58F;--hmadmin-amber:#C97900;--hmadmin-red:#D92D42;--hmadmin-blue:#2563EB;--hmadmin-purple:#7C3AED;--hmadmin-green:#168A4A;}.hmadmin-topbar{background:rgba(247,248,250,.92)}.hmadmin-logo span{box-shadow:0 0 8px rgba(14,165,143,.45)}.hmadmin-sb-badge,.hmadmin-sb-item.active,.hmadmin-badge-pro,.hmadmin-avatar{background:rgba(14,165,143,.12)}.hmadmin-sb-item em,.hmadmin-badge-cancelled,.hmadmin-btn.danger,.hmadmin-danger-note,.hmadmin-alert{background:rgba(217,45,66,.10)}.hmadmin-alert.success{border-color:var(--hmadmin-green);background:rgba(22,138,74,.12);color:var(--hmadmin-green)}.hmadmin-badge-family{background:rgba(124,58,237,.12)}.hmadmin-badge-trial{background:rgba(201,121,0,.12)}.hmadmin-badge-active{background:rgba(22,138,74,.12)}.hmadmin-badge-admin,.hmadmin-btn.blue{background:rgba(37,99,235,.12)}.hmadmin-modal-overlay{background:rgba(23,32,51,.30)}.hmadmin-btn.primary{color:#fff}.hmadmin-discovery-error{padding:14px 20px;border-bottom:1px solid var(--hmadmin-border)}.hmadmin-discovery-error strong{display:block;color:var(--hmadmin-text);font-size:13px}.hmadmin-discovery-error span{display:block;margin-top:3px;color:var(--hmadmin-text3);font-family:monospace;font-size:11px}.hmadmin-discovery-error p{margin:8px 0 0;color:var(--hmadmin-red);font-size:12px}.hmadmin-discovery-error-text{margin-top:5px;max-width:280px;color:var(--hmadmin-red);font-size:11px;line-height:1.35}
:root[data-theme="dark"] .hm-main:has(.hmadmin-wrap){background:#0A0B0D}:root[data-theme="dark"] .hmadmin-wrap{--hmadmin-bg:#0A0B0D;--hmadmin-surface:#111318;--hmadmin-surface2:#181B22;--hmadmin-surface3:#1E2229;--hmadmin-border:#252A34;--hmadmin-border2:#2E3542;--hmadmin-text:#E8EBF0;--hmadmin-text2:#9AA3B0;--hmadmin-text3:#5C6470;--hmadmin-teal:#00D4AA;--hmadmin-amber:#F5A623;--hmadmin-red:#FF4757;--hmadmin-blue:#4A9EFF;--hmadmin-purple:#A855F7;--hmadmin-green:#22C55E;}:root[data-theme="dark"] .hmadmin-topbar{background:rgba(10,11,13,.92)}:root[data-theme="dark"] .hmadmin-logo span{box-shadow:0 0 8px var(--hmadmin-teal)}:root[data-theme="dark"] .hmadmin-sb-badge,:root[data-theme="dark"] .hmadmin-sb-item.active,:root[data-theme="dark"] .hmadmin-badge-pro,:root[data-theme="dark"] .hmadmin-avatar{background:#00D4AA22}:root[data-theme="dark"] .hmadmin-sb-item em,:root[data-theme="dark"] .hmadmin-badge-cancelled,:root[data-theme="dark"] .hmadmin-btn.danger,:root[data-theme="dark"] .hmadmin-danger-note,:root[data-theme="dark"] .hmadmin-alert{background:#FF475722}:root[data-theme="dark"] .hmadmin-alert.success{background:#22C55E22;color:var(--hmadmin-green);border-color:var(--hmadmin-green)}:root[data-theme="dark"] .hmadmin-badge-family{background:#A855F722}:root[data-theme="dark"] .hmadmin-badge-trial{background:#F5A62322}:root[data-theme="dark"] .hmadmin-badge-active{background:#22C55E22}:root[data-theme="dark"] .hmadmin-badge-admin,:root[data-theme="dark"] .hmadmin-btn.blue{background:#4A9EFF22}:root[data-theme="dark"] .hmadmin-modal-overlay{background:rgba(0,0,0,.7)}:root[data-theme="dark"] .hmadmin-btn.primary{color:#000}
`}</style></>;
}
